import { Injectable, forwardRef, Inject, Injector } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { catchError, delay, finalize, map, mergeMap, retryWhen, scan, shareReplay, tap, timeout } from 'rxjs/operators';
import { BehaviorSubject, Subject, Observable, of, throwError, forkJoin } from 'rxjs';
import { LocalStorageService } from './localStorage.service';
import { EasyEatHqService } from './ee-hq.service';
import { LoyaltyApiService } from './loyalty-api.service';
import { RestaurantDetailsService } from './restaurant-details.service';
import { SyncService } from './sync-service.service';
import { country } from '../enums/language';
import {
  resolveHostedUrl as resolveHostedUrlFromConstants,
  HostedUrlKey,
} from '../../constants/hosted-api-urls';
import {
  RESTAURANT_DETAILS_SERVICE,
  RestaurantDetailsHandle,
} from './restaurant-details.token';

export const DEVICE_APP_NAME = {
  MAIN_DISPLAY: 'pos-app',
  SECONDARY_DISPLAY: 'dual-display-app',
} as const;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private restaurantDetailsService: RestaurantDetailsHandle | null = null;

  constructor(
    private httpClient: HttpClient,
    private _localStorage: LocalStorageService,
    private _hqService: EasyEatHqService,
    private loyaltyApiService: LoyaltyApiService,
    private _syncService: SyncService,
    private _injector: Injector
  ) {}

  private getRestaurantDetailsService(): RestaurantDetailsHandle {
    if (!this.restaurantDetailsService) {
      this.restaurantDetailsService = this._injector.get(RESTAURANT_DETAILS_SERVICE);
    }
    return this.restaurantDetailsService;
  }

  invalidateRestaurantDetailsCache(restaurantId?: string): void {
    this.getRestaurantDetailsService().invalidate(restaurantId);
  }

  /**
   * Dedup caches that collapse a burst of identical requests fired by multiple components on a
   * single page load into one HTTP call. Two mechanisms, driven by the TTL:
   *   - in-flight sharing: concurrent callers share the same pending observable (always on);
   *   - settle window: a fresh response is reused for `ttlMs` (skipped when `ttlMs <= 0`).
   */
  private readonly kitchenCounterCache = new Map<
    string,
    { fetchedAt: number; response: any; inFlight?: Observable<any> }
  >();
  // In-flight sharing only (ttl = 0): collapse the page-load burst without reusing a cached
  // response, so writes/refreshes always fetch fresh data once no request is pending.
  private readonly kitchenCounterDedupTtlMs = 0;

  // Table notifications are refreshed on real-time pusher events, so a stale-window skip could
  // drop a just-arrived notification — dedup only shares in-flight requests (ttl = 0).
  private readonly tableNotificationsCache = new Map<
    string,
    { fetchedAt: number; response: any; inFlight?: Observable<any> }
  >();
  private readonly tableNotificationsDedupTtlMs = 0;

  private dedupCacheKey(param: any): string {
    try {
      const token = param instanceof FormData ? param.get('token') : param?.token;
      const restId =
        param instanceof FormData ? param.get('restaurant_id') : param?.restaurant_id;
      return `${token ?? ''}|${restId ?? ''}`;
    } catch {
      return 'default';
    }
  }

  /**
   * Shared dedup wrapper. Returns the in-flight observable if one exists, else a fresh cached
   * response within `ttlMs`, else invokes `factory()` and caches the result.
   */
  private dedupRequest(
    cache: Map<string, { fetchedAt: number; response: any; inFlight?: Observable<any> }>,
    key: string,
    ttlMs: number,
    factory: () => Observable<any>
  ): Observable<any> {
    const now = Date.now();
    const cached = cache.get(key);

    if (cached?.inFlight) {
      return cached.inFlight;
    }
    if (ttlMs > 0 && cached?.response != null && now - cached.fetchedAt < ttlMs) {
      return of(cached.response);
    }

    const request$ = factory().pipe(
      tap((response) => {
        cache.set(key, { fetchedAt: Date.now(), response });
      }),
      shareReplay(1),
      finalize(() => {
        const entry = cache.get(key);
        if (entry?.inFlight === request$) {
          cache.set(key, { fetchedAt: entry.fetchedAt, response: entry.response });
        }
      })
    );

    cache.set(key, {
      fetchedAt: cached?.fetchedAt ?? 0,
      response: cached?.response ?? null,
      inFlight: request$,
    });

    return request$;
  }

  /** Clears the kitchen-counter dedup cache so the next fetch hits the API. */
  invalidateKitchenCounterCache(): void {
    this.kitchenCounterCache.clear();
  }

  /** Clears the table-notifications dedup cache so the next fetch hits the API. */
  invalidateTableNotificationsCache(): void {
    this.tableNotificationsCache.clear();
  }

  private invalidateRestaurantDetailsCacheOnWrite<T>() {
    return tap<T>(() => {
      this.getRestaurantDetailsService().invalidateForCurrentRestaurant();
    });
  }

  private resolveHostedUrl(key: HostedUrlKey): string {
    return resolveHostedUrlFromConstants(key, (k) => this._localStorage.get(k));
  }

  get phpURL(): string {
    return this.resolveHostedUrl('phpURL');
  }
  private get analyticsURL(): string {
    return this.resolveHostedUrl('analyticsURL');
  }
  get paymentUrl(): string {
    return this.resolveHostedUrl('paymentURL');
  }
  private get nestURL(): string {
    return this.resolveHostedUrl('nestURL');
  }
  private get cmmUrl(): string {
    return this.resolveHostedUrl('cmmURL');
  }

  /**
   * [PHP_TO_NEST_ROLLOUT] Phased PHP→Nest rollout switch. Returns true when the CURRENT restaurant
   * has `settings.global.is_migrated_from_php === 1`, meaning migrated endpoints should hit the new
   * Nest API instead of the legacy PHP API. Fails safe to `false` (PHP) on any error.
   *
   * HQ users: the flag is read for the active `restaurant_id` from `ee_outlets` (a single
   * `restaurant_settings` blob is stale after switching outlets).
   *
   * NOTE: intentionally duplicated (not shared) in `SectionCounterApiService` to keep the API
   * services decoupled — see that file's copy when changing this logic.
   */
  private isMigratedFromPhp(): boolean {
    // Hardcoded for Nest testing. Restore the restaurant flag check before release.
    return true;
  }

  /**
   * [PHP_TO_NEST_ROLLOUT] Ternary URL chooser for endpoints migrated to apis-nest.
   * `isMigratedFromPhp()` → new `${nestURL}${nestPath}`, else legacy `${phpURL}${phpPath}`.
   *
   * @deprecated Temporary rollout shim. To remove once every restaurant is on Nest:
   *   1) `grep [PHP_TO_NEST_ROLLOUT]` to list all call sites + PHP paths to decommission;
   *   2) replace each `this.migratedUrl(nest, php)` with `` `${this.nestURL}${nest}` ``;
   *   3) delete `migratedUrl` + `isMigratedFromPhp` and the `is_migrated_from_php` setting.
   */
  private migratedUrl(nestPath: string, phpPath: string): string {
    return this.isMigratedFromPhp()
      ? `${this.nestURL}${nestPath}`
      : `${this.phpURL}${phpPath}`;
  }

  /**
   * [PHP_TO_NEST_ROLLOUT] Normalizes a migrated response back to the legacy PHP shape.
   * Nest's `AppInterceptor` wraps handler returns that don't use `@Res()` as
   * `{ statusCode, data: <phpPayload>, status, message }`; PHP returns `<phpPayload>` raw.
   * When the Nest envelope is detected (numeric `statusCode` + object `data`), returns the inner
   * `data`; otherwise passes the response through unchanged. Safe for both PHP and Nest responses.
   *
   * Only needed for migrated endpoints whose Nest handler is enveloped (no `@Res()`) AND whose
   * callers read the payload (`res.data`). Raw (`@Res`) endpoints don't need it.
   */
  private unwrapMigrated<T = any>(res: any): T {
    if (
      res &&
      typeof res === 'object' &&
      typeof res.statusCode === 'number' &&
      res.data &&
      typeof res.data === 'object'
    ) {
      return res.data as T;
    }
    return res as T;
  }

  private listener = new Subject<any>();
  private posMenuMemory: {
    restaurant_id: string;
    lastUpdate: number | null;
    nextAvailable: number | null;
    response: any;
  } | null = null;
  /** All-items payload for the menu screen. Survives leaving for dish edit. */
  private menuListMemory: {
    restaurant_id: string;
    lastUpdate: number | null;
    nextAvailable: number | null;
    data: any;
  } | null = null;
  /**
   * The menu list component stays alive across menu routes. `reload` means a
   * dish was created, so the kept list should pick up the new row without a
   * skeleton. `item` is one saved dish to copy onto the rows already showing.
   */
  readonly keptMenuChange$ = new Subject<{ reload: boolean; item?: any }>();
  /**
   * Add endpoints do not always move `rest_menu_last_update`. While this is set,
   * the menu screen must load all-items and must not trust the stamp.
   */
  private menuAddPending = false;
  private menuAllItemsLoadCount = 0;
  private openItemsListLoadCount = 0;
  private readonly _menuAllItemsLoading$ = new BehaviorSubject<boolean>(false);
  private readonly _openItemsListLoading$ = new BehaviorSubject<boolean>(false);
  /** Emits true while any in-flight `getAllMenuList` request exists. */
  readonly menuAllItemsLoading$ = this._menuAllItemsLoading$.asObservable();
  /** Emits true while any in-flight `getAllOpenItemList` request exists. */
  readonly openItemsListLoading$ = this._openItemsListLoading$.asObservable();

  private beginMenuAllItemsLoading(): void {
    this.menuAllItemsLoadCount++;
    if (this.menuAllItemsLoadCount === 1) {
      this._menuAllItemsLoading$.next(true);
    }
  }
  private endMenuAllItemsLoading(): void {
    this.menuAllItemsLoadCount = Math.max(0, this.menuAllItemsLoadCount - 1);
    if (this.menuAllItemsLoadCount === 0) {
      this._menuAllItemsLoading$.next(false);
    }
  }
  private beginOpenItemsListLoading(): void {
    this.openItemsListLoadCount++;
    if (this.openItemsListLoadCount === 1) {
      this._openItemsListLoading$.next(true);
    }
  }
  private endOpenItemsListLoading(): void {
    this.openItemsListLoadCount = Math.max(0, this.openItemsListLoadCount - 1);
    if (this.openItemsListLoadCount === 0) {
      this._openItemsListLoading$.next(false);
    }
  }
  private hq_id = localStorage.getItem('hq_id');
  private country = localStorage.getItem('language');
  public requestCountry = {
    headers: new HttpHeaders({
      'country-code': this.country,
    }),
  };
  public httpOptions: { headers; observe } = {
    headers: new HttpHeaders({
      'Access-Control-Allow-Origin': '*',
    }),
    observe: 'response',
  };

  getRestaurantId() {
    return this._localStorage.get('restaurant_id');
  }

  isRecipeEnabled() {
    const rest_settings = this._localStorage.get('restaurant_settings');
    const allOutlets = JSON.parse(this._localStorage.get('ee_outlets'));
    if (this._hqService.isHqUser()) {
      const rest_id = this._localStorage.get('restaurant_id');
      return allOutlets.filter((o) => o.id == rest_id).length
        ? allOutlets.filter((o) => o.id == rest_id)[0].settings['global'][
            'is_recipe_enabled'
          ]
        : 0;
    } else {
      if (rest_settings && rest_settings !== 'undefined') {
        return JSON.parse(rest_settings)['global']['is_recipe_enabled'];
      }
    }
  }

  listen(): Observable<any> {
    return this.listener.asObservable();
  }

  filter(filterBy: string) {
    this.listener.next(filterBy);
  }

  getQueryParamsString(paramsObj = {}): string {
    const parts = [];
    for (const i in paramsObj) {
      if (paramsObj.hasOwnProperty(i)) {
        parts.push(i + '=' + encodeURIComponent(paramsObj[i]));
      }
    }
    return '?' + parts.join('&');
  }

  /** Reads a field from FormData or a plain object. Callers of the old PHP menu APIs send FormData. */
  private requestField(param: any, key: string): string {
    if (param == null) {
      return '';
    }
    if (typeof param.get === 'function') {
      const value = param.get(key);
      return value == null ? '' : String(value);
    }
    const value = param[key];
    return value == null ? '' : String(value);
  }

  /** CMM AppInterceptor wraps handler results as `{ statusCode, data }`. */
  private cmmList(res: any): any[] {
    return Array.isArray(res?.data) ? res.data : [];
  }

  uploadImage(param: any = {}) {
    const url = `${this.cmmUrl}/api/pos/menu/upload-image`;
    return this.httpClient.post<any>(url, param).pipe(
      map((data) => {
        return data;
      })
    );
  }
  uploadDishImage(param: any = {}) {
    const url = `${this.cmmUrl}/api/dish/upload-image`;
    return this.httpClient.post<any>(url, param).pipe(
      map((data) => {
        return data;
      })
    );
  }
  getOrderStatus(params) {
    const url = `${this.nestURL}/api/orders/order-status-tp/${params.restaurant_id}?order_type=${params.order_type}&platform=${params.platform}`;
    return this.httpClient.get<any>(url);
  }
  getOrderStatusFc(params) {
    const url = `${this.nestURL}/api/orders/foodcourt/${params.foodcourt_name}?order_type=${params.order_type}`;
    return this.httpClient.get<any>(url);
  }
  cashCollectedNest(param) {
    const url = `${this.nestURL}/api/orders/bills/cash-collected`;
    return this.httpClient.post<any>(url, param);
  }
  cashCollectedKiosk(param) {
    const url = `${this.nestURL}/api/kiosk/cash_payment`;
    return this.httpClient.post<any>(url, param, {
      headers: { 'Content-Type': 'application/json' },
    });
  }
  orderChildToKiosk(param) {
    const url = `${this.nestURL}/api/kiosk/child-to-kiosk`;
    return this.httpClient.post<any>(url, param, {
      headers: { 'Content-Type': 'application/json' },
    });
  }
  orderKiosk(param) {
    const url = `${this.nestURL}/api/kiosk/kiosk-offline-order`;
    return this.httpClient.post<any>(url, param, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /*****************  menu apis   ****************/

  getCategories(param) {
    const restaurantId = this.requestField(param, 'restaurant_id');
    if (!restaurantId) {
      return of({ status: 0, data: { categories: [] } });
    }
    const url = `${this.cmmUrl}/api/pos/category/restaurant/${encodeURIComponent(restaurantId)}`;
    return this.httpClient.get<any>(url).pipe(
      map((res) => ({
        status: res?.statusCode === 200 ? 1 : 0,
        data: { categories: this.cmmList(res) },
      })),
      catchError(() => of({ status: 0, data: { categories: [] } }))
    );
  }
  getSubcategories(param) {
    const categoryId = this.requestField(param, 'category_id');
    if (!categoryId) {
      return of({ status: 0, data: { subcategories: [] } });
    }
    const url = `${this.cmmUrl}/api/pos/subcategory/category/${encodeURIComponent(categoryId)}`;
    return this.httpClient.get<any>(url).pipe(
      map((res) => ({
        status: res?.statusCode === 200 ? 1 : 0,
        data: { subcategories: this.cmmList(res) },
      })),
      catchError(() => of({ status: 0, data: { subcategories: [] } }))
    );
  }
  addNewSubcategory(param: {
    category_id: string;
    subcategory_name: string;
    subcategory_desc: string;
    kc_list: string[];
  }) {
    const url = `${this.cmmUrl}/api/pos/subcategory/create`;
    return this.httpClient.post<any>(url, param, this.httpOptions).pipe(
      tap(() => this.markMenuAdded())
    );
  }
  deleteSubCategory(subcategoryId: string) {
    const url = `${this.cmmUrl}/api/pos/subcategory/${subcategoryId}`;
    return this.httpClient.delete<any>(url);
  }

  addNewCategory(param: {
    category_name: string;
    category_desc: string;
    is_ee?: number;
    is_grab?: number;
    is_none?: number;
  }) {
    const url = `${this.cmmUrl}/api/pos/category/create`;
    return this.httpClient.post<any>(url, param, this.httpOptions).pipe(
      tap(() => this.markMenuAdded())
    );
  }

  deleteCategory(categoryId: string) {
    const url = `${this.cmmUrl}/api/pos/category/${categoryId}`;
    return this.httpClient.delete<any>(url);
  }

  updateCategory(param: { category_id: string; fields: object }) {
    const url = `${this.cmmUrl}/api/pos/category/update`;
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }
  updateSubcategory(param: { subcategory_id: string; fields: object }) {
    const url = `${this.cmmUrl}/api/pos/subcategory/update`;
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }

  categoryBulkUpdate(param: { fields: any[] }) {
    const url = `${this.cmmUrl}/api/pos/category/bulk-update`;
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }

  subcategoryBulkUpdate(param: { fields: any[] }) {
    const url = `${this.cmmUrl}/api/pos/subcategory/bulk-update`;
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }

  /***********************************************/

  /*****************  get restaurant details   ****************/

  fetchRestaurantDetails(param) {
    const url = `${this.nestURL}/api/restaurant/v2/details`;
    return this.httpClient.post<any>(url, param);
  }

  getRestaurantDetails(param, forceRefresh = false) {
    return this.getRestaurantDetailsService().getRestaurantDetails(
      param,
      forceRefresh
    );
  }
  skipServiceCharge(param) {
    const url = `${this.nestURL}/api/orders/bills/service-charge`;
    return this.httpClient.post<any>(url, param);
  }

  getAllServices() {
    const url = `${this.nestURL}/api/restaurant/v2/services`;
    return this.httpClient.get<any>(url);
  }

  getRestaurantServices(param) {
    const url = `${this.nestURL}/api/restaurant/v2/services`;
    return this.httpClient.post<any>(url, param);
  }

  updatePassword(param) {
    const url = `${this.nestURL}/api/auth/v2/password`;
    return this.httpClient.post<any>(url, param);
  }

  updateRestaurantDetails(param) {
    const url = `${this.nestURL}/api/restaurant`;
    const restaurantId =
      param?.rest_id ||
      param?.restaurant_id ||
      this._localStorage.get('restaurant_id');
    return this.httpClient.patch<any>(url, param).pipe(
      tap((res) => {
        if (res?.statusCode === 200 || res?.statusCode === 201) {
          this.deleteRestaurantDetailsRedisCache(restaurantId).subscribe(
            () => {},
            () => {}
          );
        }
      })
    );
  }

  /** Clears PHP redis `rest_details_{restaurant_id}` after restaurant details PATCH. */
  deleteRestaurantDetailsRedisCache(restaurantId?: string) {
    const id = restaurantId || this._localStorage.get('restaurant_id');
    if (!id) {
      return of(null);
    }
    const url = `${this.phpURL}/api/redis/delete.php`;
    return this.httpClient.get<any>(url, {
      params: new HttpParams().set('key', `rest_details_${id}`),
    });
  }

  getRestaurantAddons(restaurantId: string) {
    const url = `${this.nestURL}/api/addon/${restaurantId}`;
    return this.httpClient.get<any>(url);
  }

  updateRestaurantAddons(param: { rest_id: string; addon_info: any[] }) {
    const url = `${this.nestURL}/api/addon/update`;
    return this.httpClient.patch<any>(url, param);
  }

  updateRestaurantPrinter(param) {
    const url = `${this.nestURL}/api/restaurant/printer`;
    return this.httpClient.patch<any>(url, param);
  }

  updateRestaurantLogoNest(param) {
    const url = `${
      this.nestURL
    }/api/restaurant/upload-logo/${localStorage.getItem('restaurant_id')}`;
    return this.httpClient.post<any>(url, param);
  }

  /***********************************************/

  updatePromiseState(payload: any, restaurant_id: string) {
    const url = `${this.nestURL}/api/subscription/promise/${restaurant_id}`;
    return this.httpClient.put<any>(url, payload);
  }
  /*********************************************/
  getMenu(id) {
    const url = `${this.cmmUrl}/api/menu/${id}`;
    return this.httpClient.get<any>(url);
  }
  getCmmMenuByIdNew(id) {
    const url = `${this.cmmUrl}/api/menu/menu-category-list/${id}`;
    return this.httpClient.get<any>(url);
  }
  getSubCatByIdNew(menuId,catId,order_type){
    const url = `${this.cmmUrl}/api/menu/sub-category-items/${menuId}/order_type/${order_type}/category/${catId}`;
    return this.httpClient.get<any>(url);
  }
  getSubCatId(menuId, catId, order_type) {
    const url = `${this.cmmUrl}/api/menu/${menuId}/order_type/${order_type}/category/${catId}/1`;
    return this.httpClient.get<any>(url);
  }
  addDishes(payload, menu_id, cat_id, subcat_id) {
    const url = `${this.cmmUrl}/api/menu/${menu_id}/${cat_id}/${subcat_id}/add`;
    return this.httpClient.post<any>(url, payload);
  }
  // update category subcategory availability and visibility on HQ.
  updateCatAvail(payload, category_id) {
    const rest_id = this._localStorage.get('restaurant_id');
    const url = `${this.cmmUrl}/api/menu/cat_availability/rest_id/${rest_id}/pos_category_id/${category_id}`;
    return this.httpClient.post<any>(url, payload);
  }
  updateCatVisibility(payload, category_id) {
    const rest_id = this._localStorage.get('restaurant_id');
    const url = `${this.cmmUrl}/api/menu/cat_visibility/rest_id/${rest_id}/pos_category_id/${category_id}`;
    return this.httpClient.post<any>(url, payload);
  }

  updateSubCatAvail(payload, sub_cat_id) {
    const rest_id = this._localStorage.get('restaurant_id');
    const url = `${this.cmmUrl}/api/menu/sub_cat_availability/rest_id/${rest_id}/pos_subcategory_id/${sub_cat_id}`;
    return this.httpClient.post<any>(url, payload);
  }
  updateSubCatVisibility(payload, sub_cat_id) {
    const rest_id = this._localStorage.get('restaurant_id');
    const url = `${this.cmmUrl}/api/menu/sub_cat_visibility/rest_id/${rest_id}/pos_subcategory_id/${sub_cat_id}`;
    return this.httpClient.post<any>(url, payload);
  }
  /****************************************************/

  getTables(param) {
    const url = `${this.nestURL}/api/restaurant/v2/tables`;
    return this.httpClient.post<any>(url, param);
  }

  getRestaurantMenu(param) {
    const restaurantId = this.requestField(param, 'restaurant_id');
    if (!restaurantId) {
      return of({ status: 0, data: { items: [] } });
    }
    const url = `${this.cmmUrl}/api/pos/menu/restaurant/${encodeURIComponent(restaurantId)}`;
    return this.httpClient.get<any>(url).pipe(
      map((res) => ({
        status: res?.statusCode === 200 ? 1 : 0,
        data: {
          items: this.cmmList(res).map((item) => ({
            ...item,
            name: item?.name || item?.item_name || '',
          })),
        },
      })),
      catchError(() => of({ status: 0, data: { items: [] } }))
    );
  }

  getCart(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl('/api/v2/orders/cart/get-legacy', '/api/orders/cart/get.php');
    return this.httpClient.post<any>(url, param);
  }
  updateCart(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl('/api/v2/orders/cart/update', '/api/orders/cart/update.php');
    return this.httpClient.post<any>(url, param);
  }
  updateFcCart(param) {
    const url = `${this.nestURL}/api/orders/cart`;
    return this.httpClient.patch<any>(url, param);
  }
  updateFcSpecialNotes(param) {
    const url = `${this.nestURL}/api/orders/cart/notes`;
    return this.httpClient.patch<any>(url, param);
  }
  updateFcServiceCharge(param) {
    const url = `${this.nestURL}/api/kiosk/kiosk-order/table-service`;
    return this.httpClient.put<any>(url, param);
  }
  clearCart(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl('/api/orders/cart/clear', '/api/orders/cart/clear_cart.php');
    return this.httpClient.post<any>(url, param);
  }
  placeUserOrder(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl('/api/v2/orders/create', '/api/orders/details/create.php');
    return this.httpClient.post<any>(url, param);
  }
  getCartBill(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl('/api/v2/orders/cart/cart-bill', '/api/orders/cart/get_cart_bill.php');
    return this.httpClient.post<any>(url, param);
  }

  getRiderLocation(param) {
    const url = `${this.nestURL}/api/delivery/v1/track`;
    return this.httpClient.post<any>(url, param, { observe: 'response' });
  }

  getKitchenCounter(param) {
    // [PHP_TO_NEST_ROLLOUT] enveloped Nest handler → unwrap to PHP shape
    const url = this.migratedUrl('/api/kitchen-counter/counters', '/kitchen/get_kitchen_counters.php');
    // Many components fetch kitchen counters on a single page load — collapse the burst by
    // sharing the in-flight request (ttl = 0, no cached-response reuse).
    return this.dedupRequest(
      this.kitchenCounterCache,
      this.dedupCacheKey(param),
      this.kitchenCounterDedupTtlMs,
      () =>
        this.httpClient
          .post<any>(url, param)
          .pipe(map((res) => this.unwrapMigrated(res)))
    );
  }
  confirmOrder(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl('/api/v2/orders/confirm', '/order/confirm_order.php');
    return this.httpClient.post<any>(url, param);
  }
  declineOrder(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl('/api/v2/orders/decline', '/order/decline_order.php');
    return this.httpClient.post<any>(url, param);
  }
  startPreparingOrder(param) {
    // [PHP_TO_NEST_ROLLOUT] enveloped Nest handler → unwrap to PHP shape
    const url = this.migratedUrl('/api/kitchen/start-preparing', '/kitchen/start_preparing_order.php');
    return this.httpClient
      .post<any>(url, param)
      .pipe(map((res) => this.unwrapMigrated(res)));
  }
  markPreparedOrder(param) {
    // [PHP_TO_NEST_ROLLOUT] enveloped Nest handler → unwrap to PHP shape
    const url = this.migratedUrl('/api/kitchen/mark-prepared', '/kitchen/mark_item_prepared.php');
    return this.httpClient
      .post<any>(url, param)
      .pipe(map((res) => this.unwrapMigrated(res)));
  }
  completeOrder(param) {
    // [PHP_TO_NEST_ROLLOUT] raw Nest handler (@Res) → PHP shape { status, message, loyalty_rewards? }
    const url = this.migratedUrl('/api/v2/orders/complete', '/order/order_complete.php');
    return this.httpClient.post<any>(url, param);
  }
  voidDish(param) {
    // [PHP_TO_NEST_ROLLOUT] raw Nest handler (@Res) → PHP shape { status, message, data }
    const url = this.migratedUrl(
      '/api/v2/orders/void-items',
      '/api/orders/details/void_items.php',
    );
    return this.httpClient.post<any>(url, param);
  }

  addVoidedDish(param) {
    // [PHP_TO_NEST_ROLLOUT] raw Nest handler (@Res) → PHP shape { status, message }
    const url = this.migratedUrl(
      '/api/v2/orders/unvoid-items',
      '/api/orders/details/unvoid_item.php',
    );
    return this.httpClient.post<any>(url, param);
  }

  updateCartNotes(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl('/api/v2/orders/cart/notes', '/api/orders/cart/update_special_note.php');
    return this.httpClient.post<any>(url, param);
  }
  getPastOrders(param) {
    const url = `${this.nestURL}/api/orders/history`;
    return this.httpClient.post<any>(url, param);
  }
  getTableNotifications(param) {
    // [PHP_TO_NEST_ROLLOUT] enveloped Nest handler → unwrap to PHP shape
    const url = this.migratedUrl('/api/v1/mm/table-notifications', '/mm/table-notifications/get.php');
    // Multiple components (tables-layout, sidenav, table-service, ...) fetch notifications on a
    // single page load. Share the in-flight request to collapse that burst, but do NOT reuse a
    // cached response (ttl = 0): notifications are refreshed on real-time pusher events and must
    // always fetch fresh data once no request is pending.
    return this.dedupRequest(
      this.tableNotificationsCache,
      this.dedupCacheKey(param),
      this.tableNotificationsDedupTtlMs,
      () =>
        this.httpClient
          .post<any>(url, param)
          .pipe(map((res) => this.unwrapMigrated(res)))
    );
  }

  // New APIs
  getFoodCourt(param, nameId) {
    const url = `${this.nestURL}/api/food-courts/fc/${nameId}/restaurants`;
    return this.httpClient.post<any>(url, param);
  }
  getAllCategories(rest_id) {
    const url = `${this.nestURL}/api/category/restaurant/${rest_id}`;
    return this.httpClient.get<any>(url);
  }

  getSubcategoryByCategory(catid) {
    const url = `${this.cmmUrl}/api/pos/subcategory/category/${catid}`;
    return this.httpClient.get<any>(url, { observe: 'response' });
  }
  confirmScheduleOrder(param) {
    // [PHP_TO_NEST_ROLLOUT]
    const url = this.migratedUrl(
      '/api/v2/orders/confirm-schedule',
      '/order/confirm_schedule_order.php',
    );
    return this.httpClient.post<any>(url, param);
  }

  // Nestjs api
  updateItem(param) {
    const url = `${this.cmmUrl}/api/pos/menu/item`;
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }

  updateItemPopular(param) {
    const url = `${this.cmmUrl}/api/pos/menu/item/popular`;
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }

  /* Foodcourt menu-category tagging (apis-nest). `foodcourt` is the FC slug (localStorage). */
  listFcCategories(param) {
    const url = `${this.nestURL}/api/food-court-menu/list-fc-categories`;
    return this.httpClient.post<any>(url, param);
  }

  getFcMenuItemCategory(foodcourt, item_id, restaurant_id) {
    const url = `${this.nestURL}/api/food-court-menu/${foodcourt}/items?item_id=${item_id}&restaurant_id=${restaurant_id}`;
    return this.httpClient.get<any>(url);
  }

  saveFcMenuItemCategory(param) {
    const url = `${this.nestURL}/api/food-court-menu/create-fc-menu-item`;
    return this.httpClient.post<any>(url, param);
  }

  updateFcMenuItemCategory(param) {
    const url = `${this.nestURL}/api/food-court-menu/update-fc-menu-item`;
    return this.httpClient.patch<any>(url, param);
  }

  deleteFcMenuItemCategory(param) {
    const url = `${this.nestURL}/api/food-court-menu/delete-fc-menu-item`;
    return this.httpClient.post<any>(url, param);
  }

  updateCatSubcatMenuItems(param) {
    const url = `${this.cmmUrl}/api/pos/menu/cat-subcat-items`;
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }

  addItem(param) {
    const url = `${this.cmmUrl}/api/pos/menu/item`;
    return this.httpClient.post<any>(url, param, this.httpOptions).pipe(
      tap(() => this.markMenuAdded())
    );
  }
  updateCartDiscount(param) {
    // [PHP_TO_NEST_ROLLOUT] raw Nest handler (@Res) → PHP shape { status, message, data }
    const url = this.migratedUrl(
      '/api/v2/orders/cart/mm-discount',
      '/api/orders/cart/update_mm_discount.php',
    );
    return this.httpClient.post<any>(url, param);
  }

  updateOrderDiscount(param) {
    // [PHP_TO_NEST_ROLLOUT] raw Nest handler (@Res) → PHP shape { status, message, data }
    const url = this.migratedUrl(
      '/api/v2/orders/mm-discount',
      '/order/apply_mm_discount.php',
    );
    return this.httpClient.post<any>(url, param);
  }

  updateDiscount(param) {
    // [PHP_TO_NEST_ROLLOUT] raw Nest handler (@Res) → PHP shape { status, message, data }
    const url = this.migratedUrl(
      '/api/v2/orders/cart/update-discount',
      '/api/orders/cart/update_discount.php',
    );
    return this.httpClient.post<any>(url, param);
  }

  deleteAlreadyCreatedOrder(param) {
    const url = `${this.nestURL}/api/admin/order/update`;
    return this.httpClient.post<any>(url, param);
  }

  // Printer api
  getPrintDetail(param) {
    const url = `${this.nestURL}/api/receipt/v2/receipt-data`;
    return this.httpClient.post<any>(url, param);
  }

  sortItems(param) {
    const url = `${this.cmmUrl}/api/pos/menu/items`;
    return this.httpClient.patch<any>(url, param, { observe: 'response' });
  }

  getDailySalesReport(param) {
    const url = `${this.analyticsURL}/api/report/eod-report`;
    return this.httpClient.post<any>(url, param);
  }

  getOrderAnalytics(param) {
    const url = `${this.analyticsURL}/api/analytics/dashboard/order`;
    return this.httpClient.post<any>(url, param, { observe: 'response' });
  }

  getCustomerAnalytics(param) {
    const url = `${this.analyticsURL}/api/analytics/dashboard/customer`;
    return this.httpClient.post<any>(url, param, { observe: 'response' });
  }

  getItemAnalytics(param) {
    const url = `${this.analyticsURL}/api/analytics/dashboard/item`;
    return this.httpClient.post<any>(url, param, { observe: 'response' });
  }

  getLoyaltyAnalytics(param) {
    const url = `${this.analyticsURL}/api/analytics/dashboard/loyalty`;
    return this.httpClient.post<any>(url, param, { observe: 'response' });
  }

  getTimeslotOrders(param) {
    const url = `${this.analyticsURL}/api/analytics/dashboard/order-timeslot`;
    return this.httpClient.post<any>(url, param, { observe: 'response' });
  }

  // Inventory Apis
  createInvetoryFirstTime(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/list/${this.getRestaurantId()}`;
      return this.httpClient.get<any>(url, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/create`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    }
  }
  getInventoryItems(rest_id) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/list/${this.getRestaurantId()}`;
      return this.httpClient.get<any>(url, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/${rest_id}`;
      return this.httpClient.get<any>(url, this.httpOptions);
    }
  }

  addNewProductInventory(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/create`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/product/add`;
      return this.httpClient.put<any>(url, param, this.httpOptions);
    }
  }
  updateProductInventory(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/update`;
    } else {
      url = `${this.nestURL}/api/inventory/product/update`;
    }
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }
  deleteProductInventory(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/delete`;
    } else {
      url = `${this.nestURL}/api/inventory/product/remove`;
    }
    return this.httpClient.delete<any>(url, param);
  }

  getInventoryProductDetail(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/${param.restaurant_id}/${param.product_no}`;
      return this.httpClient.get<any>(url, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/product/details`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    }
  }
  bulkUpdateItemsViaUpload(param) {
    const url = `${this.nestURL}/api/inventory/product/add/csv`;
    return this.httpClient.post<any>(url, param, this.httpOptions);
    // .pipe(map((event) => {
    //   switch (event.type) {
    //     case HttpEventType.UploadProgress:
    //       const progress = Math.round(100 * event.loaded / event.total);
    //       return { status: 'progress', message: progress };

    //     case HttpEventType.Response:
    //       return event.body;
    //     default:
    //       return `Unhandled event: ${event.type}`;
    //   }
    // }));
  }
  getInventoryListCSV(param) {
    const url = `${this.nestURL}/api/inventory/product/download`;
    return this.httpClient.post<any>(url, param);
  }
  addNewStockUpdate(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/stock-update/create`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/stock-update/add`;
      return this.httpClient.put<any>(url, param, this.httpOptions);
    }
  }
  updateNewStockUpdate(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/stock-update/update`;
      return this.httpClient.post<any>(url, param, this.httpOptions).pipe(
        retryWhen((error) => {
          return error.pipe(
            mergeMap((error) => {
              if ((error || {}).status === 503) {
                return of(error);
              }
              return throwError(error);
            }),
            scan((acc, error) => {
              if (acc > 2) throw error;
              return acc + 1;
            }, 0),
            delay(1000)
          );
        })
      );
    } else {
      url = `${this.nestURL}/api/inventory/stock-update/update`;
      return this.httpClient.patch<any>(url, param, this.httpOptions).pipe(
        retryWhen((error) => {
          return error.pipe(
            mergeMap((error) => {
              if ((error || {}).status === 503) {
                return of(error);
              }
              return throwError(error);
            }),
            scan((acc, error) => {
              if (acc > 2) throw error;
              return acc + 1;
            }, 0),
            delay(1000)
          );
        })
      );
    }
  }
  getExistingStockDetail(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/stock-update/search`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/stock-update/details`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    }
  }
  deleteItemsStockUpdate(param) {
    const url = `${this.nestURL}/api/inventory/stock-update/remove/items`;
    return this.httpClient.delete<any>(url, param);
  }
  addSupplierDetails(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/supplier/create`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/supplier/add`;
      return this.httpClient.put<any>(url, param, this.httpOptions);
    }
  }
  updateSupplierDetails(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/supplier/update`;
    } else {
      url = `${this.nestURL}/api/inventory/supplier/update`;
    }
    return this.httpClient.patch<any>(url, param, this.httpOptions);
  }
  getSupplierDetails(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${
        this.nestURL
      }/api/ingredient/supplier/${this.getRestaurantId()}/${param.supplier_no}`;
      return this.httpClient.get<any>(url, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/supplier/details`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    }
  }
  addPurchaseOrder(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/order/create`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/order/add`;
      return this.httpClient.put<any>(url, param, this.httpOptions);
    }
  }
  updatePurchaseOrder(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/order/update`;
      return this.httpClient.patch<any>(url, param, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/order/update`;
      return this.httpClient.patch<any>(url, param, this.httpOptions);
    }
  }
  deleteItemsFromOrders(param) {
    const url = `${this.nestURL}/api/inventory/order/remove/items`;
    return this.httpClient.delete<any>(url, param);
  }
  getPurchaseOrderDetail(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/order/search`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/order/details`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    }
  }

  getAllSuppliers() {
    const restId = this._localStorage.get('restaurant_id');
    const url = `${this.nestURL}/api/ingredient/supplier/${restId}`;
    return this.httpClient.get<any>(url, this.httpOptions);
  }

  searchProductIntentory(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${
        this.nestURL
      }/api/ingredient/list/${this.getRestaurantId()}?ingredient_search=${
        param.search
      }`;
      return this.httpClient.get<any>(url, this.httpOptions);
    } else {
      url = `${this.nestURL}/api/inventory/product/search`;
      return this.httpClient.post<any>(url, param, this.httpOptions);
    }
  }
  searchMenuItem(param) {
    const url = `${this.nestURL}/api/inventory/restaurant/menu/search`;
    return this.httpClient.post<any>(url, param);
  }
  //notes api
  updateQuickNotes(param) {
    const url = `${this.nestURL}/api/restaurant/v2/quick-notes`;
    return this.httpClient.post<any>(url, param);
  }

  acknowelgeTableNotification(param) {
    // [PHP_TO_NEST_ROLLOUT] enveloped Nest handler → unwrap to PHP shape
    const url = this.migratedUrl(
      '/api/v1/mm/table-notifications/acknowledge',
      '/mm/table-notifications/acknowledge.php',
    );
    return this.httpClient
      .post<any>(url, param)
      .pipe(map((res) => this.unwrapMigrated(res)));
  }
  getMappedProductDetails(param) {
    const url = `${this.nestURL}/api/inventory/product/menu`;
    return this.httpClient.post<any>(url, param);
  }
  updateMenuMapping(param) {
    const url = `${this.cmmUrl}/api/pos/menu/items`;
    return this.httpClient.patch<any>(url, param);
  }
  updateInventoryMapping(param) {
    const url = `${this.cmmUrl}/api/pos/menu/option-groups/inventory`;
    return this.httpClient.patch<any>(url, param);
  }
  updateAddonMapping(param) {
    const url = `${this.cmmUrl}/api/pos/addon/inventory`;
    return this.httpClient.patch<any>(url, param);
  }
  getPosAddonById(addonId: string) {
    const url = `${this.cmmUrl}/api/pos/addon/addon/${addonId}`;
    return this.httpClient.get<any>(url);
  }
  getPosAddonsByRestaurantId(param) {
    const url = `${this.cmmUrl}/api/pos/addon/${param.restaurant_id}`;
    return this.httpClient.get<any>(url, param);
  }
  createPosAddon(param) {
    const url = `${this.cmmUrl}/api/pos/addon`;
    return this.httpClient.post<any>(url, param);
  }
  updatePosAddon(param) {
    const url = `${this.cmmUrl}/api/pos/addon`;
    return this.httpClient.patch<any>(url, param);
  }
  deletePosAddon(addonId: string) {
    const url = `${this.cmmUrl}/api/pos/addon/${addonId}`;
    return this.httpClient.delete<any>(url);
  }

  // Filter Stock Update
  stockUpdateSearch(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/stock-update/search`;
    } else {
      url = `${this.nestURL}/api/inventory/stock-update/search`;
    }
    return this.httpClient.post<any>(url, param, this.httpOptions);
  }
  purchasereturnSearch(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/order/search`;
    } else {
      url = `${this.nestURL}/api/inventory/order/search`;
    }
    return this.httpClient.post<any>(url, param, this.httpOptions);
  }
  verifyOTP(param) {
    const url = `${this.nestURL}/api/inventory/verify-credential`;
    return this.httpClient.post<any>(url, param);
  }
  printInventoryReport(param) {
    let url;
    if (this.isRecipeEnabled()) {
      url = `${this.nestURL}/api/ingredient/download/category`;
    } else {
      url = `${this.nestURL}/api/inventory/product/download/category`;
    }
    return this.httpClient.post<any>(url, param);
  }

  downloadOrderReturnReport(type) {
    const restId = this._localStorage.get('restaurant_id');
    let param = {
      rest_id: restId,
      order_type: type,
    };
    const url = `${this.nestURL}/api/ingredient/download/purchase`;
    return this.httpClient.post<any>(url, param);
  }

  /// settlement apis
  napiAuth(param) {
    const url = `${this.nestURL}/api/auth/admin/verify`;
    return this.httpClient.post<any>(url, param);
  }
  downloadTransaction(
    download,
    start_date,
    end_date,
    restaurant_id,
    file_prefix
  ) {
    let params = { download, start_date, end_date, restaurant_id, file_prefix };
    const url = `${this.nestURL}/api/settlement/history/multiple`;
    return this.httpClient.get<any>(url + this.getQueryParamsString(params));
  }
  napiAuthToken(httpOptions) {
    const url = `${this.nestURL}/api/auth/verify-token`;
    return this.httpClient.get<any>(url, httpOptions);
  }

  getSettlementDetails(param, httpOptions) {
    const url = `${this.nestURL}/api/settlement/details`;
    return this.httpClient.post<any>(url, param, httpOptions);
  }

  getSettlementTransactions(param, httpOptions) {
    const url = `${this.nestURL}/api/transaction/details`;
    return this.httpClient.post<any>(url, param, httpOptions);
  }
  editKitchenCounter(param){
    // [PHP_TO_NEST_ROLLOUT] enveloped Nest handler → unwrap to PHP shape
    const url = this.migratedUrl('/api/kitchen-counter/edit', '/kitchen/edit_kitchen_counter.php');
    return this.httpClient
      .post<any>(url, param)
      .pipe(map((res) => this.unwrapMigrated(res)));
  }
  getCategory(param) {
    const restaurantId = this.requestField(param, 'restaurant_id');
    if (!restaurantId) {
      return of({ status: 0, categories: [] });
    }
    const url = `${this.cmmUrl}/api/pos/category/restaurant/${encodeURIComponent(restaurantId)}`;
    return this.httpClient.get<any>(url).pipe(
      map((res) => ({
        status: res?.statusCode === 200 ? 1 : 0,
        categories: this.cmmList(res),
      })),
      catchError(() => of({ status: 0, categories: [] }))
    );
  }
  getSubCategory(param) {
    const restaurantId = this.requestField(param, 'restaurant_id');
    if (!restaurantId) {
      return of({ status: 0, subcategories: [] });
    }
    const url = `${this.cmmUrl}/api/pos/subcategory/rest_ids`;
    return this.httpClient
      .post<any>(url, { restaurant_ids: [restaurantId] })
      .pipe(
        map((res) => ({
          // Nest defaults POST to 201. The kitchen-counter screen only maps this list when status is 1.
          status: res?.statusCode >= 200 && res?.statusCode < 300 ? 1 : 0,
          subcategories: this.cmmList(res),
        })),
        catchError(() => of({ status: 0, subcategories: [] }))
      );
  }
  getSubCategoryByRestIds(param) {
    const url = `${this.cmmUrl}/api/pos/subcategory/rest_ids`;
    return this.httpClient.post<any>(url, param);
  }
/** @param bustCache append `_` timestamp so CDNs/proxies do not serve a stale menu right after create/update */
  getAllMenuList(bustCache = false) {
    let url = `${this.cmmUrl}/api/pos/menu/all-items/${localStorage.getItem(
      'restaurant_id'
    )}`;
    if (bustCache) {
      url += `${url.includes('?') ? '&' : '?'}_=${Date.now()}`;
    }
    return this.httpClient.get<any>(url);
  }
  grabChangesSync(param) {
    const url = `${this.nestURL}/api/grab/menus/sync-notify`;
    return this.httpClient.post<any>(url, param);
  }
  getGrabSyncStatus(param) {
    const url = `${this.nestURL}/api/grab/menus/trace`;
    return this.httpClient.post<any>(url, param);
  }
  /** @param useMenuCache When false, adds `cached=false` alongside `open_items=true`. */
  getAllOpenItemList(useMenuCache = true) {
    this.beginOpenItemsListLoading();
    const rid = localStorage.getItem('restaurant_id');
    const q = useMenuCache ? 'open_items=true' : 'open_items=true&cached=false';
    const url = `${this.cmmUrl}/api/pos/menu/all-items/${rid}?${q}`;
    return this.httpClient.get<any>(url).pipe(
      finalize(() => this.endOpenItemsListLoading())
    );
  }

  getPaymentMethods(param) {
    const url = `${this.paymentUrl}/api/payment/get-payment-methods`;
    return this.httpClient.post<any>(url, param);
  }
  ensureBoostTerminalMethod() {
    const url = `${this.paymentUrl}/api/payment/boost-terminal/method`;
    return this.httpClient.post<any>(url, {});
  }
  ensureBoostDnqrTerminalMethod() {
    const url = `${this.paymentUrl}/api/payment/boost-dnqr-terminal/method`;
    return this.httpClient.post<any>(url, {});
  }
  initiatePayment(param) {
    const url = `${this.paymentUrl}/api/v1/payment/initiate`;
    return this.httpClient.post<any>(url, param);
  }
  initiateKioskPayment(param) {
    const url = `${this.paymentUrl}/api/v1/payment/initiate/kiosk`;
    return this.httpClient.post<any>(url, param);
  }
  getPaymentMethodsForCollection(restId, client_id) {
    const url = `${this.paymentUrl}/api/v1/payment/payment-methods/${client_id}?restaurantId=${restId}`;
    return this.httpClient.get<any>(url);
  }
  getPaymentStatus(payload) {
    const url = `${this.paymentUrl}/api/v1/payment/get-payment-detail`;
    return this.httpClient.post<any>(url, payload);
  }
  initiateRestaurantPayment(payload, countryCode?: string) {
    const url = `${this.paymentUrl}/api/v1/payment/restaurant/initiate`;
    return this.httpClient.post<any>(url, payload, {
      headers: countryCode ? { 'country-code': countryCode } : {},
    });
  }
  restaurantPaymentCallback(payload) {
    const url = `${this.paymentUrl}/api/v1/payment/restaurant/callback`;
    return this.httpClient.post<any>(url, payload);
  }
  getRestaurantPaymentStatus(paymentId: string) {
    const url = `${this.paymentUrl}/api/v1/payment/restaurant/status/${paymentId}`;
    return this.httpClient.get<any>(url);
  }
  getRestaurantPaymentMethods(countryCode: string) {
    const code = countryCode || 'MY';
    const url = `${this.paymentUrl}/api/v1/payment/restaurant/methods?country_code=${code}`;
    return this.httpClient.get<any>(url, {
      headers: { 'country-code': code },
    });
  }
  // Cash Register APIs
  addCashDetails(param) {
    const url = `${this.nestURL}/api/cash-mgt/add`;
    return this.httpClient.post<any>(url, param);
  }
  addDrawerKickDetails(param) {
    const url = `${this.nestURL}/api/cash-mgt/drawer-kick/add`;
    return this.httpClient.post<any>(url, param);
  }
  getCashRegisterAnalytics(restId, deviceId: string, counterQuery?: string, startDate?: string, endDate?: string) {
    const query = counterQuery ? counterQuery : '';
    const isReportTab = counterQuery ? 1: 0;
    let url = `${this.nestURL}/api/cash-mgt/${restId}?is_report_tab=${isReportTab}&device_ids=${query}&device_id=${deviceId}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    return this.httpClient.get<any>(url);
  }
  downloadCashTransactionXlxs(param) {
    const url = `${this.nestURL}/api/cash-mgt/download`;
    return this.httpClient.post<any>(url, param);
  }
  getCloseCashierReport(rest_id, httpOptions?, deviceId?: string) {
    const url = `${this.nestURL}/api/cash-mgt/cashier-report/${rest_id}?device_id=${deviceId}`;
    return this.httpClient.get<any>(url, httpOptions);
  }
  /* Rest Charges */
  addChargeDetails(param) {
    const url = `${this.nestURL}/api/restaurant/fees/add`;
    return this.httpClient.put<any>(url, param);
  }
  updateChargeDetails(param) {
    const url = `${this.nestURL}/api/restaurant/fees/update`;
    return this.httpClient.patch<any>(url, param);
  }
  deleteChargeDetails(param) {
    const url = `${this.nestURL}/api/restaurant/fees/delete`;
    return this.httpClient.delete<any>(url, param);
  }
  getChargeDetails(rest_id, fee_id) {
    const url = `${this.nestURL}/api/restaurant/` + rest_id + `/fee/` + fee_id;
    return this.httpClient.get<any>(url);
  }
  getAllChargesList(rest_id) {
    const url = `${this.nestURL}/api/restaurant/fees/${rest_id}`;
    return this.httpClient.get<any>(url);
  }
  getRunningOrderCount(rest_id) {
    const url = `${this.nestURL}/api/restaurant/running-order-count/${rest_id}`;
    return this.httpClient.get<any>(url);
  }
  /* end rest charges api */
  updateTablenoOfOrder(param, httpOptions?) {
    const url = `${this.nestURL}/api/orders/table-update`;
    return this.httpClient.post<any>(url, param, httpOptions);
  }
  /*Voucher Section API */
  getAllVouchers(hq_id, rest_id) {
    const url = `${this.nestURL}/api/discount-coupons/hqs/${hq_id}/restaurants/${rest_id}`;
    return this.httpClient.get<any>(url);
  }
  getSingleVoucher(hq_id, rest_id, voucher_title) {
    const url = `${this.nestURL}/api/discount-coupons/hqs/${hq_id}/restaurants/${rest_id}/vouchers/${voucher_title}`;
    return this.httpClient.get<any>(url);
  }
  addNewVoucher(param) {
    const url = `${this.nestURL}/api/discount-coupons/vouchers/upload`;
    return this.httpClient.post<any>(url, param);
  }
  updateVoucher(param) {
    const url = `${this.nestURL}/api/discount-coupons/vouchers`;
    return this.httpClient.patch<any>(url, param);
  }
  deleteVoucher(hq_id, voucher_title) {
    const url = `${this.nestURL}/api/discount-coupons/hqs/${hq_id}/vouchers/${voucher_title}`;
    return this.httpClient.delete<any>(url);
  }
  reportsVoucher(param) {
    const url = `${this.nestURL}/api/report/voucher-report`;
    return this.httpClient.post<any>(url, param);
  }
  /* loyalty redemption*/

  sendOtp(body: { dial_code: string; phone: string }) {
    const url = `${this.nestURL}/api/auth/customer/otp/send`;
    return this.httpClient.post<any>(url, body);
  }
  verifyOtp(body: { dial_code: string; phone: string; otp: string }) {
    const url = `${this.nestURL}/api/auth/customer/otp/verify`;
    return this.httpClient.post<any>(url, body);
  }
  generateToken(body: { dial_code: string; phone: string; otp: string }) {
    const url = `${this.nestURL}/api/auth/customer/login`;
    return this.httpClient.post<any>(url, body);
  }
  authToken(param) {
    const url = `${this.nestURL}/api/auth/v2/token/customer`;
    return this.httpClient.post<any>(url, param);
  }
  registerUser(body: {
    dial_code: string;
    phone: string;
    name: string;
    otp?: string;
    skip_otp?: boolean;
  }) {
    const url = `${this.nestURL}/api/auth/customer/register`;
    return this.httpClient.post<any>(url, body);
  }
  /** @deprecated Use LoyaltyApiService.getCreditUser() directly */
  getWallet(userId, id) {
    return this.loyaltyApiService.getCreditUser(userId, id);
  }
  
  /** @deprecated Use LoyaltyApiService.redeemLoyaltyOnCart() directly */
  redeemLoyaltyOnCart(params) {
    return this.loyaltyApiService.redeemLoyaltyOnCart(params);
  }
  
  redeemLoyaltyOnCartFC(params) {
    const url = `${this.nestURL}/api/food-court-loyalty/update`;
    return this.httpClient.patch<any>(url, params);
  }
  
  /** @deprecated Use LoyaltyApiService.redeemLoyaltyOnOrder() directly */
  redeemLoyaltyOnOrder(params) {
    return this.loyaltyApiService.redeemLoyaltyOnOrder(params);
  }
  
  /** @deprecated Use LoyaltyApiService.payUsingLoyalty() directly */
  payUsingLoyalty(params) {
    return this.loyaltyApiService.payUsingLoyalty(params);
  }
  /*Discount Section API */
  getAllDiscounts(rest_id) {
    const url = `${this.nestURL}/api/discount-coupons/rest/${rest_id}`;
    return this.httpClient.get<any>(url);
  }
  getClientCoupons(params) {
    const url = `${this.nestURL}/api/central-promotion/client/promotion-coupon`;
    return this.httpClient.post<any>(url,params);
  }
  getSingleDiscount(coupon_code) {
    const url = `${this.nestURL}/api/discount-coupons/${coupon_code}`;
    return this.httpClient.get<any>(url);
  }
  addNewDiscountCoupon(param) {
    const url = `${this.nestURL}/api/discount-coupons`;
    return this.httpClient.post<any>(url, param);
  }
  updateDiscountCoupon(param) {
    const url = `${this.nestURL}/api/discount-coupons`;
    return this.httpClient.patch<any>(url, param);
  }
  deleteDiscountCoupon(coupon_id) {
    const url = `${this.nestURL}/api/discount-coupons/${coupon_id}`;
    return this.httpClient.delete<any>(url);
  }
  getActiveKitchenCounterList(param) {
    // [PHP_TO_NEST_ROLLOUT] enveloped Nest handler → unwrap to PHP shape
    const url = this.migratedUrl('/api/kitchen-counter/active', '/kitchen/get_active_kitchen_counters.php');
    return this.httpClient
      .post<any>(url, param)
      .pipe(map((res) => this.unwrapMigrated(res)));
  }
  /*get list of active kitchen counters for foodcourt*/
  getFCActiveKitchenCountersList(rest_id){
    const url = `${this.nestURL}/api/offline-platforms/fp/fc-kitchen-counters/${rest_id}`;
    return this.httpClient.get<any>(url);
  }

  /*add price cut apis*/
  checkItemPriceCutStatus(param) {
    const url = `${this.nestURL}/api/discount-coupons/price-cut/status`;
    return this.httpClient.post<any>(url, param);
  }
  addPriceCut(param) {
    const url = `${this.nestURL}/api/discount-coupons/price-cut`;
    return this.httpClient.post<any>(url, param);
  }
  editPriceCut(param) {
    const url = `${this.nestURL}/api/discount-coupons/price-cut`;
    return this.httpClient.patch<any>(url, param);
  }

  /*Platform commission apis*/
  getAllAvailablePlatformsOnEasyeat(rest_id) {
    const url = `${this.nestURL}/api/restaurant/easyeat-offline-platforms/easyeat?restaurant_id=${rest_id}`;
    return this.httpClient.get<any>(url);
  }
  getAllPlatformsForRestaurant(rest_id) {
    const url = `${this.nestURL}/api/restaurant/offline-platforms/${rest_id}`;
    return this.httpClient.get<any>(url);
  }
  getOfflinePlatformDetails(platform_id, rest_id) {
    const url = `${this.nestURL}/api/restaurant/offline-platforms/${rest_id}/${platform_id}`;
    return this.httpClient.get<any>(url);
  }
  addOfflinePlatform(param) {
    const url = `${this.nestURL}/api/restaurant/offline-platforms`;
    return this.httpClient.put<any>(url, param);
  }
  updateOfflinePlatform(param) {
    const url = `${this.nestURL}/api/restaurant/offline-platforms`;
    return this.httpClient.patch<any>(url, param);
  }
  deleteOfflinePlatform(platform_id, rest_id) {
    const url = `${this.nestURL}/api/restaurant/offline-platforms/${rest_id}/${platform_id}`;
    return this.httpClient.delete<any>(url);
  }
  /*End*/

  /*Staff Center API*/
  getAllStaffList(param) {
    const url = `${this.nestURL}/api/staff/all`;
    return this.httpClient.post<any>(url, param);
  }
  addNewStaff(param) {
    const url = `${this.nestURL}/api/staff`;
    return this.httpClient.post<any>(url, param);
  }
  editStaff(param) {
    const url = `${this.nestURL}/api/staff`;
    return this.httpClient.patch<any>(url, param);
  }
  bulkUpdateStaff(param) {
    const url = `${this.nestURL}/api/staff/bulk-update-roles`;
    return this.httpClient.patch<any>(url, param);
  }
  deleteStaff(staff_id, httpOptions) {
    const url = `${this.nestURL}/api/staff/${staff_id}`;
    return this.httpClient.delete<any>(url, httpOptions);
  }
  getSingleStaffInfo(param) {
    const url = `${this.nestURL}/api/staff/details`;
    return this.httpClient.post<any>(url, param);
  }
  viewPin(key) {
    const url = `${this.nestURL}/api/auth/view/${key}`;
    return this.httpClient.get<any>(url);
  }
  uploadStaffImage(param, httpOptions) {
    const url = `${this.nestURL}/api/staff/photo`;
    return this.httpClient.post<any>(url, param, httpOptions);
  }
  resendStaffPasswordResetLink(param) {
    const url = `${this.nestURL}/api/staff/password-reset`;
    return this.httpClient.post<any>(url, param);
  }
  updateAvailGrab(param) {
    const url = `${this.nestURL}/api/grab/update/modifier`;
    return this.httpClient.post<any>(url, param);
  }

  /* clock in clock out apis */

  getAllStaffsList(param) {
    const url = `${this.nestURL}/api/staff/all`;
    return this.httpClient.post<any>(url, param);
  }

  verifyStaff(param) {
    const url = `${this.nestURL}/api/auth/verify`;
    return this.httpClient.post<any>(url, param);
  }

  markAttendance(param, httpOptions) {
    const url = `${this.nestURL}/api/staff/attendance`;
    return this.httpClient.post<any>(url, param, httpOptions);
  }

  getStaffStatus(user_id, httpOptions) {
    const url = `${this.nestURL}/api/staff/attendance/${user_id}/status`;
    return this.httpClient.get<any>(url, httpOptions);
  }
  getAllDesignations(hq_id) {
    const url = `${this.nestURL}/api/staff/designation/${hq_id}`;
    return this.httpClient.get<any>(url);
  }
  getAllRoles(payload) {
    const url = `${this.nestURL}/api/roles/details`;
    return this.httpClient.post<any>(url, payload);
  }
  generateRandomStaffPin(hq_id) {
    const url = `${this.nestURL}/api/staff/pin/${hq_id}`;
    return this.httpClient.get<any>(url);
  }

  /* End */
  /* get running orders nest */
  getCurrentOrders(rest_id, search, filter_by, restaurant_ids, order_paid?) {
    let params: any = { search, filter_by, restaurant_ids };
    if (order_paid !== undefined) {
      params.order_paid = order_paid;
    }
    const url = `${this.nestURL}/api/orders/active/${rest_id}`;
    return this.httpClient.get<any>(url + this.getQueryParamsString(params));
  }
  /* get order details nest */
  getOrderDetailsNest(order_id) {
    const url = `${this.nestURL}/api/orders/${order_id}`;
    return this.httpClient.get<any>(url).pipe(
      tap((response) => {
        if (this._syncService.shouldSkipOrderSyncFromFetch()) return;
        const statusCode = Number(response?.statusCode);
        if (statusCode !== 200) return;
        const restId = this._localStorage.get('restaurant_id');
        const isCentralCashier = this._syncService.isCentralCashier();
        const syncId = isCentralCashier
          ? response?.data?.fc_order_id || order_id
          : order_id;
        if (!restId || !syncId) return;
        if (this._syncService.isSplitBillModeActive()) {
          const orderSplit = this._syncService.getSplitBillOrderSplit(syncId);
          if (!orderSplit?.length) {
            return;
          }
        }
        const orderStatus = Number(response?.data?.order_status);
        const isCompletedState = [4, 6, 9].includes(orderStatus);
        const idKey = isCentralCashier ? 'fc_order' : 'order';
        const syncCall = isCompletedState
          ? this._syncService.syncOrderCompleteState(
              restId,
              syncId,
              response?.data,
              response?.data?.bill,
              idKey
            )
          : this._syncService.syncOrderUpdateState(
              restId,
              syncId,
              response?.data,
              response?.data?.bill,
              {},
              idKey
            );
        syncCall.subscribe(() => {}, () => {});
      })
    );
  }

  updateOrderDetails(param) {
    const url = `${this.nestURL}/api/orders/`;
    return this.httpClient.post<any>(url, param);
  }

  revertOrder(payload) {
    const url = `${this.nestURL}/api/orders/revert`;
    return this.httpClient.patch<any>(url, payload);
  }

  updateCustomerDetails(param) {
    const url = `${this.nestURL}/api/orders/customer-details/`;
    return this.httpClient.post<any>(url, param);
  }
  /*Food panda integration end points*/
  foodPandaLogin(param) {
    const url = `${this.nestURL}/api/offline-platforms/fp/login`;
    return this.httpClient.post<any>(url, param);
  }
  getAllFoodpandaMapping(
    rest_id,
    unmapped_items: string,
    unmapped_items_count: string,
    platform
  ) {
    const url = `${this.nestURL}/api/offline-platforms/${rest_id}/${
      (this.isKiosk() ? 'foodcourt/' : '') + platform
    }/menu`;
    return this.httpClient.get<any>(url, {
      params: new HttpParams()
        .set('unmapped_items', unmapped_items)
        .set('unmapped_items_count', unmapped_items_count),
    });
  }
  updateAllFoodpandaMapping(param, platform) {
    const url = `${this.nestURL}/api/offline-platforms/${
      (this.isKiosk() ? 'foodcourt/' : '') + platform
    }/menu`;
    return this.httpClient.patch<any>(url, param);
  }
  isFoodPandaOrderSynced(rest_id, platform) {
    const url = `${this.nestURL}/api/offline-platforms/${rest_id}/${platform}/sync`;
    return this.httpClient.get<any>(url);
  }
  createFoodPandaOrders(param, platform) {
    const url = `${this.nestURL}/api/offline-platforms/${
      (this.isKiosk() ? 'foodcourt/' : '') + platform
    }/order/create`;
    return this.httpClient.post<any>(url, param);
  }
  /* Grab Food  integration end points*/
  grabLogin(param) {
    const url = `${this.nestURL}/api/offline-platforms/gf/login`;
    return this.httpClient.post<any>(url, param);
  }
  intiateGrab(param) {
    const url = `${this.nestURL}/api/grab/auth/initiate`;
    return this.httpClient.post<any>(url, param);
  }
  grabStatus(param) {
    const url = `${this.nestURL}/api/grab/status`;
    return this.httpClient.post<any>(url, param);
  }
  /*item level discount*/
  itemLevelDiscount(param, key) {
    const url =
      key === 'order'
        ? `${this.nestURL}/api/orders/discount`
        : `${this.nestURL}/api/orders/cart/discount`;
    return this.httpClient.patch<any>(url, param);
  }

  /*All promotions template endpoint*/
  getAllPromoTemplates() {
    const url = `${this.nestURL}/api/promotion/templates`;
    return this.httpClient.get<any>(url);
  }
  /*user segmentation*/
  getCustomerGroup(hq_id, use_pagination, page_size?, page_no?) {
    let params = { use_pagination, page_size, page_no };
    const url = `${this.nestURL}/api/restaurant-hqs/user-segmentation/v1/${hq_id}`;
    return this.httpClient.get<any>(url + this.getQueryParamsString(params));
  }
  getRoi(param) {
    const url = `${this.nestURL}/api/roi-page/metrics`;
    return this.httpClient.post<any>(url, param);
  }
  createCustomerGroup(param) {
    const url = `${this.nestURL}/api/restaurant-hqs/user-segmentation/create`;
    return this.httpClient.post<any>(url, param);
  }
  createCampaign(param) {
    const url = `${this.nestURL}/api/marketing-campaign/create-campaign`;
    return this.httpClient.post<any>(url, param).pipe(timeout(10000), // 10 sec
    catchError(error => {
      return throwError(error);
    }))
  }
  getCampaignCost(payload): Observable<any> {
    const url = `${this.nestURL}/api/marketing-campaign/get-campaign-cost`;
    return this.httpClient.post<any>(url, payload);
  }
  getTemplate(type) {
    const url = `${this.nestURL}/api/marketing-campaign/get-sms?campaign_filter=${type}`;
    return this.httpClient.get<any>(url);
  }
  getLink(id) {
    const url = `${this.nestURL}/api/marketing-campaign/generate-google-review/${id}`;
    return this.httpClient.get<any>(url);
  }
  getTemplateById(id) {
    const url = `${this.nestURL}/api/marketing-campaign/get-sms/${id}`;
    return this.httpClient.get<any>(url);
  }
  deleteCustomer(id) {
    const url = `${this.nestURL}/api/restaurant-hqs/user-segmentation/${id}/deleteUserHqSegment`;
    return this.httpClient.delete<any>(url);
  }

  getCampaign(
    hq_id,
    page_size?,
    page_no?,
    campaign_filter?,
    restaurant_filter?
  ) {
    let params = new HttpParams()
      .set('page_size', page_size || '')
      .set('page_no', page_no || '')
      .set('campaign_filter', campaign_filter || '');
    // Add multiple restaurant_filter parameters to the HttpParams
    if (Array.isArray(restaurant_filter) && restaurant_filter.length > 0) {
      restaurant_filter.forEach((id) => {
        params = params.append('restaurant_filter', id.toString());
      });
    }

    const url = `${this.nestURL}/api/marketing-campaign/hq-campaigns-list/${hq_id}`;
    return this.httpClient.get<any>(url, { params: params });
  }

  getCampaignTemplate() {
    let getCountry = localStorage.getItem('country');
    let params = {
      country: 'Malaysia',
    };
    if (getCountry === country.id.country) {
      params['country'] = 'Indonesia';
    } else {
      params['country'] = 'Malaysia';
    }
    const url = `${this.nestURL}/api/marketing-campaign/get-sms/v1`;
    return this.httpClient.post<any>(url, params);
  }
  getCampaignViaId(id) {
    const url = `${this.nestURL}/api/marketing-campaign/get-draft-campaign-details/${id}`;
    return this.httpClient.get<any>(url);
  }
  deleteCampaign(id) {
    const url = `${this.nestURL}/api/marketing-campaign/${id}`;
    return this.httpClient.delete<any>(url);
  }
  onetime(param) {
    const url = `${this.nestURL}/api/marketing-campaign/send-sms`;
    return this.httpClient.post<any>(url, param).pipe(timeout(10000), // 10 sec
    catchError(error => {
      return throwError(error);
    }))
  }
  automate(params) {
    const url = `${this.nestURL}/api/marketing-campaign/send-automated-campaign-sms`;
    return this.httpClient.post<any>(url, params).pipe(timeout(10000), // 10 sec
    catchError(error => {
      return throwError(error);
    }))
  }

  /* loyalty — delegated to LoyaltyApiService (loyaltyURL) */
  
  /** @deprecated Use LoyaltyApiService.createLoyalty() directly */
  createLoyalty(params) {
    return this.loyaltyApiService.createLoyalty(params);
  }
  
  /** @deprecated Use LoyaltyApiService.updateLoyalty() directly */
  updateLoyalty(params) {
    return this.loyaltyApiService.updateLoyalty(params);
  }
  
  /** @deprecated Use LoyaltyApiService.getLoyaltySettings() directly */
  getLoyalty(hq_id) {
    return this.loyaltyApiService.getLoyaltySettings(hq_id);
  }
  
  /** @deprecated Use LoyaltyApiService.getMember() directly */
  getMember(param) {
    return this.loyaltyApiService.getMember(param);
  }
  
  /** @deprecated Use LoyaltyApiService.getAvgValue() directly */
  getAvgValue(param) {
    return this.loyaltyApiService.getAvgValue(param);
  }
  
  /** @deprecated Use LoyaltyApiService.getRewards() directly */
  getRewards(param) {
    return this.loyaltyApiService.getRewards(param);
  }
  
  /** @deprecated Use LoyaltyApiService.getRevenue() directly */
  getRevenue(param) {
    return this.loyaltyApiService.getRevenue(param);
  }
  /*
  All HQ endpoints
  */
  getAllOutletList(mall, searchTerm) {
    const url = `${this.nestURL}/api/restaurant-hqs`;
    return this.httpClient.get<any>(url, {
      params: new HttpParams().set('name', searchTerm),
    });
  }

  getRestaurantHq(hqId: string) {
    return this.httpClient.get<any>(`${this.nestURL}/api/restaurant-hqs/${hqId}`);
  }
  updateRestaurantInfos(param): any {
    const url = `${this.nestURL}/api/restaurant`;
    return this.httpClient.patch<any>(url, param);
  }
  getRolesOfHQ(param) {
    const url = `${this.nestURL}/api/roles/details`;
    return this.httpClient.post<any>(url, param);
  }

  getRoleDetails(id) {
    const url = `${this.nestURL}/api/roles/${id}`;
    return this.httpClient.get<any>(url);
  }
  getAllPermissions(posType:string) {
    const url = `${this.nestURL}/api/permissions?foodcourt=${posType}`;
    return this.httpClient.get<any>(url);
  }
  getRolePermissions(id, query, posType) {
    const url = `${this.nestURL}/api/permissions/${id}?foodcourt=${posType}`;
    return this.httpClient.get<any>(url, {
      params: new HttpParams().set('type', query),
    });
  }
  getAllHQStats(payload) {
    const url = `${this.nestURL}/api/restaurant-hqs/orders`;
    return this.httpClient.post<any>(url, payload);
  }
  deleteRole(id) {
    const url = `${this.nestURL}/api/roles/${id}`;
    return this.httpClient.delete<any>(url);
  }
  updateRole(param) {
    const url = `${this.nestURL}/api/roles`;
    return this.httpClient.patch<any>(url, param);
  }
  addRole(param) {
    const url = `${this.nestURL}/api/roles`;
    return this.httpClient.post<any>(url, param);
  }
  getRestaurantToken(rest_id: string) {
    const url = `${this.nestURL}/api/admin/restaurants/${rest_id}/url?get_token=1`;
    return this.httpClient.get<any>(url);
  }
  getAllHQDishes(searchTerm) {
    const url = `${this.nestURL}/api/restaurant-hqs/menu/all`;
    return this.httpClient.get<any>(url, {
      params: new HttpParams().set('search', searchTerm),
    });
  }
  /*
    New Variant Group Endpoints
  */
  getAllVariantGroups(param) {
    const url = `${this.cmmUrl}/api/pos/menu/option-groups/all`;
    return this.httpClient.post<any>(url, param);
  }
  getVarintGroupDetails(id) {
    const url = `${this.cmmUrl}/api/pos/menu/option-groups/${id}`;
    return this.httpClient.get<any>(url);
  }
  addNewVariantGroup(param) {
    const url = `${this.cmmUrl}/api/pos/menu/option-groups`;
    return this.httpClient.post<any>(url, param);
  }
  editVariantGroup(param) {
    const url = `${this.cmmUrl}/api/pos/menu/option-groups`;
    return this.httpClient.patch<any>(url, param);
  }
  getGrabChangesData(id) {
    const url = `${this.nestURL}/api/audit-logs/gf/rest/${id}`;
    return this.httpClient.get<any>(url);
  }
  showPublish(id) {
    const url = `${this.nestURL}/api/audit-logs/gf/publish/${id}`;
    return this.httpClient.get<any>(url);
  }
  outOfStockCatSub(param) {
    const url = `${this.nestURL}/api/audit-logs/gf/cat-sub-hide-show`;
    return this.httpClient.post<any>(url, param);
  }
  grabPause(param) {
    const url = `${this.nestURL}/api/grab/pause/store`;
    return this.httpClient.post<any>(url, param);
  }
  /* Ask Grab whether an order may be cancelled, and for the reasons it will accept. */
  grabCancelEligibility(param) {
    const url = `${this.nestURL}/api/grab/order/cancel_eligibility`;
    return this.httpClient.post<any>(url, param);
  }
  /* Cancel a GrabFood order at Grab. The backend re-checks eligibility before sending it,
     and cancels our own copy of the order once Grab confirms. */
  grabCancelOrder(param) {
    const url = `${this.nestURL}/api/grab/order/mark_cancel_order`;
    return this.httpClient.post<any>(url, param);
  }
  /* Tell Grab the food is ready. Safe to repeat — the
     backend answers already_marked instead of calling Grab a second time. */
  grabMarkOrderReady(param) {
    const url = `${this.nestURL}/api/grab/order/ready_for_delivery`;
    return this.httpClient.post<any>(url, param);
  }
  /* Remove or reduce items on a GrabFood order. Goes to Grab first and only voids our
     own copy once Grab accepts — unlike voidDish(), which only touches our copy. */
  grabApplyEdit(param) {
    const url = `${this.nestURL}/api/grab/order/apply_edit`;
    return this.httpClient.post<any>(url, param);
  }
  getItemDetails(item_id, variation_type) {
    const url = `${this.cmmUrl}/api/pos/menu/${item_id}`;
    return this.httpClient.get<any>(url, {
      params: new HttpParams().set('formatVariation', variation_type),
    });
  }
  checkOffline(param) {
    const url = `${this.nestURL}/api/restaurant/last-active`;
    return this.httpClient.put<any>(url, param);
  }
  getMappedItemsToVariant(group_id) {
    const url = `${this.cmmUrl}/api/pos/menu/option-groups/${group_id}/items`;
    return this.httpClient.get<any>(url);
  }
  getRestMenuLastUpdate(restaurant_id: string) {
    const url = `${this.cmmUrl}/api/pos/menu/last-update/${restaurant_id}`;
    return this.httpClient.get<any>(url);
  }

  /** Same stamp create-order stores beside its in-memory menu. */
  parseMenuFreshness(res: any): {
    lastUpdate: number | null;
    nextAvailable: number | null;
  } {
    return this.extractRestMenuFreshness(res);
  }

  storeMenuList(restaurantId: string, data: any): void {
    if (!restaurantId || !data) return;
    const previous =
      this.menuListMemory?.restaurant_id === restaurantId ? this.menuListMemory : null;
    let snapshot: any;
    try {
      snapshot = JSON.parse(JSON.stringify(data));
    } catch {
      return;
    }
    this.menuListMemory = {
      restaurant_id: restaurantId,
      lastUpdate: previous ? previous.lastUpdate : null,
      nextAvailable: previous ? previous.nextAvailable : null,
      data: snapshot,
    };
  }

  /** An add succeeded. The stamp is not a reliable signal, so the list must reload. */
  private markMenuAdded(): void {
    this.menuAddPending = true;
    this.keptMenuChange$.next({ reload: true });
  }

  menuAddRequiresReload(): boolean {
    return this.menuAddPending;
  }

  clearMenuAddReload(): void {
    this.menuAddPending = false;
  }

  /** A new dish/combo was written. The kept menu list reloads that row set in place. */
  noteMenuListChanged(): void {
    this.markMenuAdded();
  }

  peekMenuList(restaurantId: string): any | null {
    if (!restaurantId || this.menuListMemory?.restaurant_id !== restaurantId) return null;
    return this.menuListMemory.data || null;
  }

  menuListFreshness(restaurantId: string): {
    lastUpdate: number | null;
    nextAvailable: number | null;
  } | null {
    if (!restaurantId || this.menuListMemory?.restaurant_id !== restaurantId) return null;
    return {
      lastUpdate: this.menuListMemory.lastUpdate,
      nextAvailable: this.menuListMemory.nextAvailable,
    };
  }

  setMenuListFreshness(
    restaurantId: string,
    freshness: { lastUpdate: number | null; nextAvailable: number | null }
  ): void {
    if (!freshness || this.menuListMemory?.restaurant_id !== restaurantId) return;
    this.menuListMemory.lastUpdate = freshness.lastUpdate;
    this.menuListMemory.nextAvailable = freshness.nextAvailable;
  }

  editStoredMenu(restaurantId: string, edit: (data: any) => void): void {
    if (
      !restaurantId ||
      this.menuListMemory?.restaurant_id !== restaurantId ||
      !this.menuListMemory.data
    ) {
      return;
    }
    edit(this.menuListMemory.data);
  }

  /** Copies one saved dish into the stored menu, including a category move. */
  replaceStoredMenuItem(restaurantId: string, fresh: any): void {
    if (!fresh?.id) return;
    let applied = false;
    this.editStoredMenu(restaurantId, (data) => {
      const located = this.findStoredMenuItem(data, fresh.id);
      if (!located) return;
      applied = true;
      const fields = [
        'item_name',
        'description',
        'item_code',
        'status',
        'visible',
        'next_available',
        'price',
        'price_text',
        'original_price',
        'original_price_text',
        'delivery_price',
        'delivery_price_text',
        'takeaway_price',
        'takeaway_price_text',
        'grab_price',
        'grab_price_text',
        'price_pricecut',
        'price_pricecut_text',
        'delivery_pricecut',
        'delivery_pricecut_text',
        'takeaway_pricecut',
        'takeaway_pricecut_text',
        'image',
        'image_type',
        'open_price',
        'unit',
        'base_qty',
        'is_grab',
        'is_ee',
        'is_none',
        'ot_list',
        'category_id',
        'subcategory_id',
        'availability',
        'availability_days',
        'preparation_minutes',
        'filters',
        'is_popular',
      ];
      fields.forEach((key) => {
        if (fresh[key] !== undefined && fresh[key] !== null) {
          located.item[key] = fresh[key];
        }
      });
      if (fresh.gvariations === null || typeof fresh.gvariations === 'string') {
        located.item.gvariations = fresh.gvariations;
      }
      const nextSubcatId = fresh.subcategory_id;
      if (!nextSubcatId || String(located.subcat.id) === String(nextSubcatId)) return;
      located.subcat.items = (located.subcat.items || []).filter(
        (item) => String(item.id) !== String(fresh.id)
      );
      const dest = this.findStoredSubcat(data, nextSubcatId);
      if (!dest) return;
      if (!Array.isArray(dest.subcat.items)) dest.subcat.items = [];
      located.item.category_id = dest.cat.id;
      located.item.subcategory_id = dest.subcat.id;
      dest.subcat.items.push(located.item);
    });
    this.keptMenuChange$.next(applied ? { reload: false, item: fresh } : { reload: true });
  }

  private findStoredMenuItem(data: any, itemId: string): { item: any; cat: any; subcat: any } | null {
    for (const cat of data?.categories || []) {
      for (const subcat of cat.subcategories || []) {
        const item = (subcat.items || []).find((entry) => String(entry?.id) === String(itemId));
        if (item) return { item, cat, subcat };
      }
    }
    return null;
  }

  private findStoredSubcat(data: any, subcatId: string): { cat: any; subcat: any } | null {
    for (const cat of data?.categories || []) {
      const subcat = (cat.subcategories || []).find(
        (entry) => String(entry?.id) === String(subcatId)
      );
      if (subcat) return { cat, subcat };
    }
    return null;
  }

  getMenuItems(param) {
    const url = `${this.cmmUrl}/api/pos/menu`;
    const restaurant_id = param && param.restaurant_id;
    if (!restaurant_id) {
      return this.httpClient.post<any>(url, param);
    }

    const fetchMenu = (known?: { lastUpdate: number | null; nextAvailable: number | null }) =>
      this.httpClient.post<any>(url, param).pipe(
        mergeMap((res) => {
          const stamp$ =
            known && known.lastUpdate != null && Number.isFinite(Number(known.lastUpdate))
              ? of(known)
              : this.getRestMenuLastUpdate(restaurant_id).pipe(
                  map((tsRes) => this.extractRestMenuFreshness(tsRes)),
                  catchError(() => of({ lastUpdate: null, nextAvailable: null }))
                );
          return stamp$.pipe(
            map((freshness) => {
              if (res && res.statusCode === 200 && res.data) {
                this.posMenuMemory = {
                  restaurant_id,
                  lastUpdate: freshness.lastUpdate,
                  nextAvailable: freshness.nextAvailable,
                  response: res,
                };
              }
              return res;
            })
          );
        })
      );

    const cached = this.posMenuMemory;
    if (
      cached &&
      cached.restaurant_id === restaurant_id &&
      cached.response &&
      cached.response.data &&
      cached.response.data.items
    ) {
      return this.getRestMenuLastUpdate(restaurant_id).pipe(
        mergeMap((tsRes) => {
          const freshness = this.extractRestMenuFreshness(tsRes);
          const stampSame =
            freshness.lastUpdate != null &&
            cached.lastUpdate != null &&
            Number(freshness.lastUpdate) === Number(cached.lastUpdate);
          if (stampSame && !this.menuAvailabilityDue(cached.nextAvailable)) {
            if (freshness.nextAvailable !== cached.nextAvailable) {
              this.posMenuMemory = {
                restaurant_id: cached.restaurant_id,
                lastUpdate: cached.lastUpdate,
                nextAvailable: freshness.nextAvailable,
                response: cached.response,
              };
            }
            return of(cached.response);
          }
          return fetchMenu(freshness);
        }),
        catchError(() => fetchMenu())
      );
    }

    return forkJoin([
      this.getRestMenuLastUpdate(restaurant_id).pipe(
        map((tsRes) => this.extractRestMenuFreshness(tsRes)),
        catchError(() => of({ lastUpdate: null, nextAvailable: null }))
      ),
      this.httpClient.post<any>(url, param),
    ]).pipe(
      mergeMap(([freshness, menu]) => {
        if (!(menu && menu.statusCode === 200 && menu.data)) {
          return of(menu);
        }
        const stamp$ =
          freshness && freshness.lastUpdate != null
            ? of(freshness)
            : this.getRestMenuLastUpdate(restaurant_id).pipe(
                map((tsRes) => this.extractRestMenuFreshness(tsRes)),
                catchError(() => of({ lastUpdate: null, nextAvailable: null }))
              );
        return stamp$.pipe(
          map((resolved) => {
            this.posMenuMemory = {
              restaurant_id,
              lastUpdate: resolved.lastUpdate,
              nextAvailable: resolved.nextAvailable,
              response: menu,
            };
            return menu;
          })
        );
      })
    );
  }

  private menuAvailabilityDue(nextAvailable: number | null | undefined): boolean {
    return nextAvailable != null && Math.floor(Date.now() / 1000) >= Number(nextAvailable);
  }

  private extractRestMenuFreshness(res: any): {
    lastUpdate: number | null;
    nextAvailable: number | null;
  } {
    return {
      lastUpdate: this.extractRestMenuLastUpdate(res),
      nextAvailable: this.extractRestMenuNextAvailable(res),
    };
  }

  private extractRestMenuLastUpdate(res: any): number | null {
    const raw =
      res && res.data
        ? res.data.rest_menu_last_update
        : res && res.rest_menu_last_update;
    return this.extractEpoch(raw);
  }

  private extractRestMenuNextAvailable(res: any): number | null {
    const raw =
      res && res.data ? res.data.next_available : res && res.next_available;
    return this.extractEpoch(raw);
  }

  private extractEpoch(raw: any): number | null {
    if (raw === undefined || raw === null || raw === '') {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  deleteVariantGroup(group_id) {
    const url = `${this.cmmUrl}/api/pos/menu/option-groups/${group_id}`;
    return this.httpClient.delete<any>(url);
  }
  /*email invoice Api*/

  emailInvoiceToUser(param) {
    const url = `${this.nestURL}/api/orders/email-invoice`;
    return this.httpClient.post<any>(url, param);
  }

  /*Split Bill Apis*/
  private publishOrderSplitSync(orderId: string, guests: any[]): void {
    const restId = this._localStorage.get('restaurant_id');
    if (!restId || !orderId || !guests?.length) {
      return;
    }
    this.getOrderDetailsNest(orderId).subscribe((orderResponse) => {
      const orderDetails =
        Number(orderResponse?.statusCode) === 200 ? orderResponse?.data : undefined;
      this._syncService
        .syncOrderSplitFromGuestsApi(restId, orderId, guests, orderDetails)
        ?.subscribe(() => {}, () => {});
    });
  }

  getSplitBillDetails(order_id) {
    const url = `${this.nestURL}/api/orders/bills/${order_id}/guests`;
    return this.httpClient.get<any>(url).pipe(
      tap((response) => {
        const statusCode = Number(response?.statusCode);
        if (statusCode !== 200 || !Array.isArray(response?.data) || !response.data.length) {
          return;
        }
        this.publishOrderSplitSync(order_id, response.data);
      })
    );
  }
  updateSplitBillDetails(payload) {
    const url = `${this.nestURL}/api/orders/bills/guests`;
    return this.httpClient.post<any>(url, payload).pipe(
      tap((response) => {
        const statusCode = Number(response?.statusCode);
        if (statusCode !== 201 || response?.data?.status !== 1) {
          return;
        }
        const guests = response?.data?.data;
        if (!Array.isArray(guests) || !guests.length) {
          return;
        }
        const orderId = payload?.order_id;
        if (!orderId) {
          return;
        }
        this.publishOrderSplitSync(orderId, guests);
      })
    );
  }

  /*End Split bill*/

  /*New Settlement endpoints*/

  getAllPastSettlementHistory(
    page_size,
    page_no,
    start_date,
    end_date,
    date_format,
    restaurant_ids?,
    download?
  ) {
    const url = `${this.nestURL}/api/settlement/history`;
    let params = { page_size, page_no, start_date, end_date, download };
    restaurant_ids &&
      restaurant_ids.length &&
      (params['restaurant_ids'] = restaurant_ids);
    date_format && (params['date_format'] = date_format);
    return this.httpClient.get<any>(url + this.getQueryParamsString(params));
  }

  getPastSettlementHistoryById(id: string, date_format, download?: number) {
    let params = { date_format };
    download && (params['download'] = download);
    const url = `${this.nestURL}/api/settlement/history/${id}`;
    return this.httpClient.get<any>(url + this.getQueryParamsString(params));
  }

  getAdhocSettlementHistoryById(id: string) {
    const url = `${this.nestURL}/api/settlements-adhoc/history/${id}`;
    return this.httpClient.get<any>(url);
  }
  downloadAdhocSettlement(id: string) {
    const params = {
      settlement_ids: [id],
      download: 1,
    };
    const url = `${this.nestURL}/api/settlements-adhoc/settlement/details`;
    return this.httpClient.post<any>(url, params, {
      responseType: 'blob' as 'json',
    });
  }

  /*End Settlement endpoints*/
  getAllAvailableFloorsAndTables(
    rest_id: string,
    floor: number,
    floor_id?: string | null
  ) {
    const rId = localStorage.getItem('restaurant_id');
    return this.httpClient.get(
      `${this.nestURL}/api/restaurant/new/${
        rest_id ? rest_id : rId
      }/tables?floor=${floor}&floor_id=${floor_id || null}`
    );
  }

  updateFPAutoSyncOrderNotes(payload) {
    return this.httpClient.post(
      `${this.nestURL}/api/orders/special-notes`,
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );
  }

  updateRestaurantSettings(payload) {
    return this.httpClient
      .patch(`${this.nestURL}/api/admin/restaurants/settings`, payload)
      .pipe(this.invalidateRestaurantDetailsCacheOnWrite());
  }

  fetchRestaurantDetailsWithPayload(payload) {
    return this.httpClient.post(
      `${this.nestURL}/api/restaurant/details`,
      payload
    );
  }

  uploadStaticPaymentQr(restaurantId: string, file: File) {
    const url = `${this.nestURL}/api/restaurant/details/static-payment-qr/${restaurantId}`;
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<any>(url, formData);
  }

  /*get user details*/
  getUserDetailsByContact(payload) {
    return this.httpClient.post(`${this.nestURL}/api/user/details`, payload);
  }

  updateUserDetails(payload) {
    return this.httpClient.patch(`${this.nestURL}/api/user/details`, payload);
  }

  /*NEST Table CRUD API*/
  getRestaurantSettings(rest_id: string, options = {}) {
    return this.httpClient.get(
      `${this.nestURL}/api/admin/restaurants/${rest_id}/settings`,
      options
    );
  }

  hqStaffSignUp(payload) {
    return this.httpClient.post(
      `${this.nestURL}/api/auth/staff/update-password`,
      payload
    );
  }

  getAllPresetCoupons(payload) {
    return this.httpClient.post(
      `${this.nestURL}/api/discount-coupons/coupons`,
      payload
    );
  }

  applyThirdPartyVoucher(payload) {
    return this.httpClient.post(
      `${this.nestURL}/api/discount-coupons/vouchers`,
      payload
    );
  }

  applyThirdPartyVoucherFoodcourt(payload) {
    return this.httpClient.post(
      `${this.nestURL}/api/discount-coupons/food-court-vouchers`,
      payload
    );
  }

  removeThirdPartyVoucher(payload) {
    return this.httpClient.post(
      `${this.nestURL}/api/discount-coupons/vouchers/remove`,
      payload
    );
  }

  getOrdersForMultipleRestaurants(
    rest_id = null,
    search,
    filter_by,
    restaurant_ids,
    new_response
  ) {
    let params = { search, filter_by, restaurant_ids, new_response };
    const url = `${this.nestURL}/api/orders/active/${rest_id}`;
    return this.httpClient.get<any>(url + this.getQueryParamsString(params));
  }

  deleteItem(item_id) {
    return this.httpClient.delete(`${this.cmmUrl}/api/pos/menu/${item_id}`);
  }

  // get HQ details by HQ ID
  getHQAccessDetails(hq_id) {
    const url = `${this.nestURL}/api/admin/hq/${hq_id}/url`;
    return this.httpClient.get<any>(url);
  }

  updateStaffInOutTime(payload) {
    return this.httpClient.post(
      `${this.nestURL}/api/staff/attendance/update`,
      payload
    );
  }

  checkUniqueName(body) {
    const hqId = this._localStorage.get('hq_id');
    const url = `${this.cmmUrl}/api/common/${hqId}/check-unique?${body.key}=${body.value}`;
    return this.httpClient.get(url);
  }

  getDeviceStatus(param) {
    const url = `${this.nestURL}/api/restaurant/default-device/${param.restaurant_id}/${param.deviceId}`;
    return this.httpClient.get<any>(url, param);
  }

  updateOrderPaymentMethod(payload) {
    return this.httpClient.patch<any>(
      `${this.nestURL}/api/orders/payment/update`,
      payload
    );
  }

  downloadCashierReportData(payload) {
    return this.httpClient.post<any>(
      `${this.nestURL}/api/cash-mgt/cash-report/download`,
      payload
    );
  }

  getCashierReportDataByEpoch(payload) {
    return this.httpClient.post<any>(
      `${this.nestURL}/api/cash-mgt/cash-report/view`,
      payload
    );
  }

  createReferral(payload) {
    return this.httpClient.post<any>(
      `${this.nestURL}/api/discount-coupons/referral/details/create`,
      payload
    );
  }

  getReferralDetails(payload) {
    return this.httpClient.post<any>(
      `${this.nestURL}/api/discount-coupons/referral/details`,
      payload
    );
  }

  updateReferral(payload) {
    return this.httpClient.patch<any>(
      `${this.nestURL}/api/discount-coupons/referral/details/`,
      payload
    );
  }

  getFcCart(payload) {
    return this.httpClient.post<any>(
      `${this.nestURL}/api/orders/cart`,
      payload
    ).pipe(tap((response) => this.publishFcCartSync(response)));
  }

  /** CART_UPDATED for Central Cashier. Same body as restaurant, keyed by fc_order_id. */
  private publishFcCartSync(response: any): void {
    if (Number(response?.statusCode) !== 200) {
      return;
    }
    const restId = this._localStorage.get('restaurant_id');
    const data = response?.data;
    const fcOrderId = this.resolveFcOrderSyncId(data);
    if (!restId || !fcOrderId || !data) {
      return;
    }
    const cart = data.cart ?? data;
    const cartBill = data.cart_bill ?? data.bill ?? data;
    this._syncService
      .syncCartState(restId, fcOrderId, cart, cartBill, 'fc_order')
      .subscribe(() => {}, () => {});
  }

  /** fc_order_id once the order exists, otherwise the food-court cart token. */
  private resolveFcOrderSyncId(data?: any): string {
    const fcOrderId =
      data?.fc_order_id ||
      this._localStorage.get('pos_order_id') ||
      this._localStorage.get('fc_cart_token') ||
      '';
    return String(fcOrderId || '');
  }
  getAllKioskOrders(foodcourt, search?, filter_by? , order_paid?) {
    const url = `${this.nestURL}/api/orders/kiosk-orders/${foodcourt}`;
    const params: any = { search , filter_by };
    if (order_paid !== undefined) {
      params.order_paid = order_paid;
    }
    return this.httpClient.get<any>(url + this.getQueryParamsString(params));
  }
  getKioskOrderDetails(id, pastOrders: string = '') {
    return this.httpClient.get<any>(
      `${this.nestURL}/api/orders/kiosk-order/${id}?past_orders=${pastOrders}`
    ).pipe(tap((response) => this.publishKioskOrderSync(id, response)));
  }

  /** ORDER_UPDATED / ORDER_COMPLETED for Central Cashier, using fc_order_id. */
  private publishKioskOrderSync(id: string, response: any): void {
    if (this._syncService.shouldSkipOrderSyncFromFetch()) {
      return;
    }
    if (Number(response?.statusCode) !== 200) {
      return;
    }
    const restId = this._localStorage.get('restaurant_id');
    const fcOrderId = response?.data?.fc_order_id || id;
    if (!restId || !fcOrderId) {
      return;
    }
    if (this._syncService.isSplitBillModeActive()) {
      const orderSplit = this._syncService.getSplitBillOrderSplit(fcOrderId);
      if (!orderSplit?.length) {
        return;
      }
    }
    const orderStatus = Number(response?.data?.order_status);
    const isCompletedState = [4, 6, 9].includes(orderStatus);
    const billing = response?.data?.bill ?? response?.data;
    const syncCall = isCompletedState
      ? this._syncService.syncOrderCompleteState(
          restId,
          fcOrderId,
          response?.data,
          billing,
          'fc_order'
        )
      : this._syncService.syncOrderUpdateState(
          restId,
          fcOrderId,
          response?.data,
          billing,
          {},
          'fc_order'
        );
    syncCall.subscribe(() => {}, () => {});
  }
  acceptKioskOrder(payload) {
    return this.httpClient.post<any>(
      `${this.nestURL}/api/kiosk/accept`,
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );
  }
  declineKioskOrder(payload) {
    return this.httpClient.post<any>(
      `${this.nestURL}/api/kiosk/decline`,
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );
  }
  // Central promotion
  getAllOutletsDiscounts(param) {
    const url = `${this.nestURL}/api/central-promotion/all-hq-coupon-info`;
    return this.httpClient.post<any>(url, param);
  }
  getCentralCouponInfo(param) {
    const url = `${this.nestURL}/api/central-promotion/coupon-info`;
    return this.httpClient.post<any>(url, param);
  }
  addNewDiscountCentralCoupon(param) {
    const url = `${this.nestURL}/api/central-promotion/create`;
    return this.httpClient.post<any>(url, param);
  }
  updateDiscountCentralCoupon(param) {
    const url = `${this.nestURL}/api/central-promotion/update`;
    return this.httpClient.post<any>(url, param);
  }
  deleteDiscountCentralCoupon(param) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: param,
    };
    const url = `${this.nestURL}/api/central-promotion/delete`;
    return this.httpClient.delete<any>(url, httpOptions);
  }
  addPriceCutCentralCoupon(param) {
    const url = `${this.nestURL}/api/central-promotion/price-cut/create`;
    return this.httpClient.post<any>(url, param);
  }
  updatePriceCutCentralCoupon(param) {
    const url = `${this.nestURL}/api/central-promotion/price-cut/update`;
    return this.httpClient.post<any>(url, param);
  }
  getAllRestaurantsInfoByHQID(hq_id) {
    const url = `${this.cmmUrl}/api/pos/menu/hq_menu/${hq_id}`;
    return this.httpClient.get<any>(url);
  }

  getEEInfo(hq_id: string) {
    const url = `${this.nestURL}/api/rockset/ee-value/${hq_id}`;
    return this.httpClient.get<any>(url);
  }
  getAnalytics(restaurant_id: string) {
    const url = `${this.nestURL}/api/rockset/ee-earnings/${restaurant_id}`;
    return this.httpClient.get<any>(url);
  }

  // Pinelabs apis
  initiatePLPayment(param: Record<any, any>) {
    const url = `${this.paymentUrl}/api/v1/payment/initiate`;
    return this.httpClient.post<any>(url, param);
  }
  cancelPLPayment(param: Record<any, any>) {
    const url = `${this.paymentUrl}/api/v1/pinelab/cancel`;
    return this.httpClient.post<any>(url, param);
  }

  pollingPLPayment(param: Record<any, any>) {
    const url = `${this.paymentUrl}/api/v1/pinelab/polling`;
    return this.httpClient.post<any>(url, param);
  }
  recordBoostTerminalPayment(param: Record<any, any>) {
    const url = `${this.paymentUrl}/api/v1/payment/boost-terminal/record`;
    return this.httpClient.post<any>(url, param);
  }
  recordBoostTerminalAdjustment(param: Record<any, any>) {
    const url = `${this.paymentUrl}/api/v1/payment/boost-terminal/adjustment`;
    return this.httpClient.post<any>(url, param);
  }
  // negative balance

  dismissBanner(restaurant_id: string) {
    const url = `${this.nestURL}/api/negative-balance/dismiss-banner/${restaurant_id}`;
    return this.httpClient.put<any>(url, '');
  }
  dismissBannerSubscription(restaurant_id: string) {
    const url = `${this.nestURL}/api/subscription/${restaurant_id}/dismiss-banner`;
    return this.httpClient.patch<any>(url, '');
  }
  getInvoices(
    restaurantIds?: string,
    category?: string,
    startDate?: string,
    endDate?: string
  ) {
    let params = { restaurantIds, category, startDate, endDate };
    const url = `${this.nestURL}/api/invoices/transactions`;
    return this.httpClient.get<any>(url + this.getQueryParamsString(params));
  }
  dismissBannerHq(hq_id: string) {
    const url = `${this.nestURL}/api/negative-balance/dismiss-banner/hq/${hq_id}`;
    return this.httpClient.put<any>(url, '');
  }
  subscriptionPlans(restaurant_id: string, planType: string) {
    const url = `${this.nestURL}/api/subscription/plans/${restaurant_id}?planType=${planType}`;
    return this.httpClient.get<any>(url);
  }
  getBanner(restaurant_id: string) {
    const url = `${this.nestURL}/api/negative-balance/${restaurant_id}`;
    return this.httpClient.get<any>(url);
  }
  talkToexpert(restaurant_id: string, type: string) {
    const url = `${this.nestURL}/api/negative-balance/talk-to-expert/${restaurant_id}?type=${type}`;
    return this.httpClient.get<any>(url);
  }
  genrateTicketExpert(payload, restaurant_id: string) {
    const url = `${this.nestURL}/api/negative-balance/create-ticket/${restaurant_id}`;
    return this.httpClient.post<any>(url, payload);
  }
  getHqBalance(hq_id: string) {
    const url = `${this.nestURL}/api/negative-balance/unpaid-restaurants/${hq_id}`;
    return this.httpClient.get<any>(url);
  }
  downloadNegativeCurrentBalanceReport(hq_id: string, restaurantId?: string) {
    const url = `${this.nestURL}/api/negative-balance/download-current-balance/${hq_id}/${restaurantId}`;
    return this.httpClient.post<any>(url, {});
  }
  initiateSettlementPayment(payload) {
    const url = `${this.nestURL}/api/settlements-payment/initiate`;
    return this.httpClient.post<any>(url, payload);
  }
  createSettlementBill(payload) {
    const url = `${this.nestURL}/api/settlements-payment/reoccur-initiate`;
    return this.httpClient.post<any>(url, payload);
  }
  downloadBalanceBill(restaurant_id: string) {
    const url = `${this.nestURL}/api/negative-balance/download/${restaurant_id}`;
    return this.httpClient.post<any>(url, '');
  }
  pledge(payload: any, restaurant_id: string) {
    const url = `${this.nestURL}/api/negative-balance/promise/${restaurant_id}`;
    return this.httpClient.put<any>(url, payload);
  }

  getAllDelayPrintingData(restId: string) {
    const url = `${this.nestURL}/api/print/pending-events?restaurant_id=${restId}`;
    return this.httpClient.get<any>(url);
  }

  // Handling Refunds

  addRefunds(payload) {
    const url = `${this.nestURL}/api/refunds/add`;
    return this.httpClient.post<any>(url, payload);
  }

  getAllRefundOptions(orderId: string) {
    const url = `${this.nestURL}/api/refunds/refund_option/${orderId}`;
    return this.httpClient.get<any>(url);
  }

  isRefundBtnVisible(orderId: string) {
    const url = `${this.nestURL}/api/orders/give_refund_visible/${orderId}`;
    return this.httpClient.get<any>(url);
  }
  // fc discount
  getDiscounts(payload) {
    const url = `${this.nestURL}/api/fc/coupons/active`;
    return this.httpClient.post<any>(url, payload);
  }
  applyDiscount(payload) {
    const url = `${this.nestURL}/api/fc/coupons/apply`;
    return this.httpClient.post<any>(url, payload);
  }
  removeDiscount(payload) {
    const url = `${this.nestURL}/api/fc/coupons/remove-rest`;
    return this.httpClient.post<any>(url, payload);
  }
  removeAllDiscount(payload) {
    const url = `${this.nestURL}/api/fc/coupons/remove-all`;
    return this.httpClient.post<any>(url, payload);
  }

  getScanNWinData(rest_id: string) {
    const url = `${this.nestURL}/api/scan-and-win/get-leaderboard-data?rest_id=${rest_id}`;
    return this.httpClient.get<any>(url);
  }

  //redis cache api to get dates of scan-n-win
  getScanNWinDates(){
    const url = `${this.nestURL}/api/redis/get-scan-and-win-dates`
    return this.httpClient.get<any>(url);
  }

  isKiosk() {
    const restSettings = JSON.parse(
      this._localStorage.get('restaurant_settings') || '{}'
    );
    return restSettings['global']['is_kiosk'] || 0;
  }

  createHubspotContact(payload) {
    const url = `${this.nestURL}/api/visitor-identification/create`;
    return this.httpClient.post<any>(url, payload);
  }

  // CAMPAING DETAILS

  // Gets all campaigns details
  getAllCampaign(restId:string){
    const url = `${this.nestURL}/api/campaigns?restaurant_id=${restId}`;
    return this.httpClient.get<any>(url);
  }

  //Submit Interest in Campaign
  showInterest(payload){
    const url = `${this.nestURL}/api/campaign-interest`;
    return this.httpClient.post<any>(url, payload);
  }
  //get eligibilty details
  submitEligibleForm(payload: any, restaurantId: string) {
    const url = `${this.nestURL}/api/eligibility/form/${restaurantId}`;
    return this.httpClient.post<any>(url, payload);
  }
  fetchLoanEligibility(restId:string){
    const url = `${this.nestURL}/api/eligibility/${restId}`;
    return this.httpClient.get<any>(url)
  }

  getAllReceiptPrinters(restId, deviceId) {
    const url = `${this.nestURL}/api/receipt-printer/${restId}?device_id=${deviceId}`;
    return this.httpClient.get(url);
  }

  getMultiDevicePrint(restaurantId: string):Observable<any> {
    const url = `${this.nestURL}/api/kitchen-counter/get-multi-device-print/${restaurantId}`;
    return this.httpClient.get<any>(url);
  }

  updateMultiDevicePrint(payload: { restaurant_id: string; device_id: string }):Observable<any> {
    const url = `${this.nestURL}/api/kitchen-counter/update-multi-device-print`;
    return this.httpClient.patch<any>(url, payload);
  }

  downloadVariantSample() {
    const url = `${this.cmmUrl}/api/variant/download-template`;
    const param = {
      hq_id:this.hq_id
    }
    return this.httpClient.post<any>(url,param);
  }

  downloadMenuSample(){
    const url = `${this.cmmUrl}/api/bulk-upload/download-template`;
    const param = {
      hq_id:this.hq_id
    }
    return this.httpClient.post<any>(url,param);
  }

  bulkUploadVariants(file: File) {
    const url = `${this.cmmUrl}/api/variant/bulk-upload`;
    const formData = new FormData();
    formData.append("hq_id", this.hq_id);
    formData.append("file", file);
    return this.httpClient.post<any>(url,formData);
  }

  getCmmCategoryList(payload) {
    const url = `${this.cmmUrl}/api/menu/categories`;
    return this.httpClient.post<any>(url,payload);
  }

  createOpenItem(payload) {
    const url = `${this.cmmUrl}/api/dish/open-item`;
    return this.httpClient.post<any>(url,payload);
  }

  bulkEditVariant(){
    const hq_id = this.hq_id;
    const url = `${this.cmmUrl}/api/variant/variant-edit-list/${hq_id}`;
    return this.httpClient.get<any>(url);
  }

  deleteOpenItem(dishId: string){
    const payload = {
      hq_id: this.hq_id,
      item_id: dishId
    }
    const url = `${this.cmmUrl}/api/dish/open-item/${dishId}`;
    return this.httpClient.delete<any>(url, {body: payload});
  }

  updateOpenItem(payload){
    const url = `${this.cmmUrl}/api/dish/open-item`;
    return this.httpClient.put<any>(url,payload);
  }

  publishVariant(payload){
    const url = `${this.cmmUrl}/api/variant/bulk-create-json`;
    return this.httpClient.post<any>(url,payload);
  }

  // Grab Self Integration
  getSetupIntegrationData():Observable<any>{
    const restaurantId = this.getRestaurantId();
    const url = `${this.nestURL}/api/grab-self-integrate/grab-status/${restaurantId}`;
    return this.httpClient.get<any>(url);
  }

  initiateSetup():Observable<any>{
    const restaurantId = this.getRestaurantId();
    const payload = {
      restaurant_id: restaurantId,
      selfServeIntegration:1
    }
    const url = `${this.nestURL}/api/grab/auth/initiate`;
    return this.httpClient.post<any>(url,payload);
  }

  getMenuData():Observable<any>{
    const restaurantId = this.getRestaurantId();
    const url = `${this.nestURL}/api/grab/get/grab-items-price/${restaurantId}`;
    return this.httpClient.get<any>(url);
  }

  updateMenuData(payload: any):Observable<any>{
    const restaurantId = this.getRestaurantId();
    const url = `${this.nestURL}/api/grab/update/grab-items-price/${restaurantId}`;
    return this.httpClient.patch<any>(url,payload);
  }

  getVariantData():Observable<any>{
    const restaurantId = this.getRestaurantId();
    const url = `${this.nestURL}/api/grab/get-variants/${restaurantId}`;
    return this.httpClient.get<any>(url);
  }

  updateVariantData(payload: any):Observable<any>{
    const url = `${this.nestURL}/api/grab/update-variants`;
    return this.httpClient.patch<any>(url,payload);
  }

  updateGrabTimings(payload: any):Observable<any>{
    const url = `${this.nestURL}/api/grab-self-integrate/grab-timings-add`;
    return this.httpClient.post<any>(url,payload);
  }

  selfServeIntegration(payload: any):Observable<any>{
    const url = `${this.nestURL}/api/grab-self-integrate/self-serve-journey`;
    return this.httpClient.post<any>(url,payload);
  }

  getCdsConfiguration():Observable<any>{
    const restaurantId = this.getRestaurantId();
    const url = `${this.nestURL}/api/customer-display/cds-configurations/${restaurantId}`;
    return this.httpClient.get<any>(url);
  }

  getCdsConfigurationData(configId: string):Observable<any>{
    const restaurantId = this.getRestaurantId();
    const url = `${this.nestURL}/api/customer-display/order-data/${restaurantId}/${configId}`;
    return this.httpClient.get<any>(url);
  }

  generateRedirectionLink(restaurantId: string):Observable<any>{
    const url = `${this.nestURL}/api/customer-display/redirection-link/${restaurantId}`;
    return this.httpClient.get<any>(url);
  }

  getDualDisplayConfigsByHq(hqId: string):Observable<any>{
    const url = `${this.nestURL}/api/dual-display/v1/display-configurations/hq/${hqId}`;
    return this.httpClient.get<any>(url);
  }

  getDualDisplayConfigsByRestaurant(restaurantId: string):Observable<any>{
    const url = `${this.nestURL}/api/dual-display/v1/display-configurations/restaurant/${restaurantId}`;
    return this.httpClient.get<any>(url);
  }

  createDualDisplayConfig(restaurantId: string, payload: any):Observable<any>{
    const url = `${this.nestURL}/api/dual-display/v1/display-configurations/restaurant/${restaurantId}/create-setting`;
    return this.httpClient.post<any>(url, payload);
  }

  saveDualDisplayConfig(restaurantId: string, configId: string, payload: any):Observable<any>{
    const url = `${this.nestURL}/api/dual-display/v1/display-configurations/restaurant/${restaurantId}/config/${configId}/update-setting`;
    return this.httpClient.post<any>(url, payload);
  }

  getRestaurantDevices(restaurantId: string, appName: string = DEVICE_APP_NAME.MAIN_DISPLAY): Observable<any> {
    const url = `${this.nestURL}/api/restaurant/device/list`;
    return this.httpClient
      .post<any>(url, { restaurant_id: restaurantId, app_name: appName })
      .pipe(
        map((response) => ({
          ...response,
          data: (response?.data || []).filter((device) => !device?.is_deleted),
        }))
      );
  }

  getRestaurantDevice(restaurantId: string, deviceId: string): Observable<any> {
    const url = `${this.nestURL}/api/restaurant/device/${restaurantId}/${deviceId}`;
    return this.httpClient.get<any>(url);
  }

  getRestaurantDeviceByUid(
    restaurantId: string,
    deviceId: string,
    appName: string = DEVICE_APP_NAME.MAIN_DISPLAY
  ): Observable<any | null> {
    const findMatch = (response: any): any | null => {
      const payload = response?.data ?? response;
      const candidates = Array.isArray(payload)
        ? payload
        : [payload?.device ?? payload];
      return (
        candidates.find(
          (device) =>
            String(device?.uid || '').toLowerCase() ===
              String(deviceId || '').toLowerCase() &&
            (!device.restaurant_id || device.restaurant_id === restaurantId) &&
            (!device.app_name || device.app_name === appName)
        ) || null
      );
    };

    return this.getRestaurantDevice(restaurantId, deviceId).pipe(
      map(findMatch),
      catchError(() => of(null)),
      mergeMap((device) =>
        device
          ? of(device)
          : this.getRestaurantDevices(restaurantId, appName).pipe(
              map(findMatch),
              catchError(() => of(null))
            )
      )
    );
  }

  saveRestaurantDevice(payload: {
    restaurant_id: string;
    uid: string;
    platform: string;
    device_name: string;
  }): Observable<any> {
    const url = `${this.nestURL}/api/restaurant/device`;
    return this.httpClient.post<any>(url, payload);
  }

  deleteRestaurantDevice(payload: {
    restaurant_id: string;
    uid: string;
    platform: string;
    device_name: string;
  }): Observable<any> {
    const url = `${this.nestURL}/api/restaurant/device`;
    return this.httpClient.post<any>(url, { ...payload, is_deleted: true });
  }

  createDisplayBanner(hqId: string, payload: any): Observable<any> {
    const url = `${this.nestURL}/api/dual-display/v1/display-banners/hq/${hqId}/create-banner`;
    return this.httpClient.post<any>(url, payload);
  }

  updateDisplayBanner(hqId: string, bannerId: string, payload: any): Observable<any> {
    const url = `${this.nestURL}/api/dual-display/v1/display-banners/hq/${hqId}/banner/${bannerId}/update-banner`;
    return this.httpClient.post<any>(url, payload);
  }

  getDisplayBannersList(hqId: string, filters: any = {}, page: number = 1, size: number = 10): Observable<any> {
    const url = `${this.nestURL}/api/dual-display/v1/display-banners/hq/${hqId}/list`;
    const payload = {
      filters: filters,
      page: page,
      size: size
    };
    return this.httpClient.post<any>(url, payload);
  }

 getSignedImageUrl(hqId: string, body: object): Observable<any> {
  const url = `${this.nestURL}/api/restaurant-hqs/${hqId}/signed-image-url`;
  return this.httpClient.post<any>(url, body);
}
}
